import { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import API, { formatWIB, formatWIBTime } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Play, ArrowLeft, DownloadSimple, ArrowUp, ArrowDown, FunnelSimple, X, ArrowClockwise, Warning, CheckCircle, CircleNotch, Clock, Image, PaperPlaneTilt, Keyboard, CaretLeft, CaretRight, CheckSquare, Square, ArrowsLeftRight, Lightning } from "@phosphor-icons/react";

function formatIDR(num) {
  if (!num && num !== 0) return "-";
  return new Intl.NumberFormat("id-ID").format(num);
}

// Step icons mapping
const STEP_ICONS = {
  browser_launch: "browser",
  open_login: "login",
  solve_captcha: "captcha",
  submit_login: "login",
  enter_pin: "pin",
  wait_otp: "otp",
  verify_otp: "verify",
  navigate_tx: "nav",
  set_daterange: "date",
  scrape_data: "scrape",
  parse_records: "parse",
  match_records: "match",
  finalize: "done",
};

function JobProgressPanel({ jobId, onComplete, onDismiss }) {
  const [progress, setProgress] = useState(null);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaSubmitting, setCaptchaSubmitting] = useState(false);
  const [captchaError, setCaptchaError] = useState("");
  const [zoomOpen, setZoomOpen] = useState(false);
  const pollRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const captchaInputRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const elapsedRef = useRef(null);
  const isDone = useRef(false);

  const fetchProgress = useCallback(async () => {
    try {
      const { data } = await API.get(`/jobs/${jobId}/progress`);
      setProgress(data);
      if ((data.status === "completed" || data.status === "error") && !isDone.current) {
        isDone.current = true;
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
        if (onComplete) setTimeout(onComplete, 1500);
      }
      if (data.status === "waiting_captcha" && captchaInputRef.current) {
        captchaInputRef.current.focus();
      }
    } catch (e) {
      console.error("Progress poll error:", e);
    }
  }, [jobId, onComplete]);

  useEffect(() => {
    isDone.current = false;
    startTimeRef.current = Date.now();
    setElapsed(0);
    fetchProgress();
    pollRef.current = setInterval(fetchProgress, 2500);
    elapsedRef.current = setInterval(() => {
      if (!isDone.current) setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [fetchProgress]);

  const handleCaptchaSubmit = async () => {
    if (!captchaInput.trim()) return;
    setCaptchaSubmitting(true);
    setCaptchaError("");
    try {
      await API.post(`/jobs/${jobId}/captcha-input`, { captcha_text: captchaInput.trim() });
      setCaptchaInput("");
    } catch (e) {
      setCaptchaError(e.response?.data?.detail || "Gagal mengirim CAPTCHA");
    } finally {
      setCaptchaSubmitting(false);
    }
  };

  if (!progress) return null;

  const allSteps = progress.all_steps || [];
  const completedSteps = progress.steps?.filter(s => s.status === "completed") || [];
  const completedKeys = completedSteps.map(s => s.key);
  const currentStep = progress.current_step;
  const estRemaining = progress.est_remaining_seconds || 0;
  const isFinished = progress.status === "completed" || progress.status === "error";
  const pct = isFinished ? 100 : (allSteps.length > 0 ? Math.round((completedKeys.length / allSteps.length) * 100) : 0);

  const getStepStatus = (stepKey) => {
    if (completedKeys.includes(stepKey)) return "completed";
    if (stepKey === currentStep && progress.status === "waiting_captcha") return "waiting";
    if (stepKey === currentStep && progress.status !== "error") return "running";
    const errorStep = progress.steps?.find(s => s.key === stepKey && s.status === "error");
    if (errorStep) return "error";
    const waitStep = progress.steps?.find(s => s.key === stepKey && s.status === "waiting_captcha");
    if (waitStep) return "waiting";
    return "pending";
  };

  const getStepExtra = (stepKey) => {
    const steps = progress.steps?.filter(s => s.key === stepKey) || [];
    return steps[steps.length - 1] || null;
  };

  const formatTime = (secs) => {
    if (secs < 60) return `${secs}d`;
    return `${Math.floor(secs / 60)}m ${secs % 60}d`;
  };

  const screenshotUrl = progress.has_screenshot
    ? `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/error-screenshot`
    : null;

  return (
    <div data-testid="job-progress-panel" className="border border-zinc-200 rounded-sm overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-3 text-white flex items-center justify-between ${progress.status === "waiting_captcha" ? "bg-amber-600" : "bg-zinc-900"}`}>
        <div className="flex items-center gap-3">
          {progress.status === "error" ? (
            <Warning size={18} weight="fill" className="text-red-400" />
          ) : progress.status === "completed" ? (
            <CheckCircle size={18} weight="fill" className="text-emerald-400" />
          ) : progress.status === "waiting_captcha" ? (
            <Keyboard size={18} weight="fill" className="text-white" />
          ) : (
            <CircleNotch size={18} className="animate-spin" />
          )}
          <div>
            <p className="text-sm font-semibold">
              {progress.status === "error" ? "Job Gagal" : progress.status === "completed" ? "Job Selesai" : progress.status === "waiting_captcha" ? "Menunggu Input CAPTCHA" : "Job Sedang Berjalan..."}
            </p>
            <p className="text-[10px] text-white/70 font-mono">
              {progress.status === "waiting_captcha" ? "AI gagal membaca CAPTCHA. Ketik CAPTCHA secara manual di bawah." : progress.current_step_label || "Memulai..."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono">
          <span className="text-zinc-400"><Clock size={12} className="inline mr-1" />{isFinished ? "Selesai dalam" : "Berlalu"}: {formatTime(elapsed)}</span>
          {progress.status === "running" && estRemaining > 0 && <span className="text-zinc-400">~{formatTime(estRemaining)} tersisa</span>}
          <span className="text-white font-semibold">{pct}%</span>
          {isFinished && onDismiss && (
            <button onClick={onDismiss} className="text-zinc-400 hover:text-white ml-2" data-testid="dismiss-progress-btn">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-zinc-200 relative">
        <div
          className={`h-full transition-all duration-500 ease-out ${progress.status === "error" ? "bg-red-500" : progress.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Steps */}
      <div className="p-4 space-y-1 max-h-[340px] overflow-y-auto">
        {allSteps.map((step, idx) => {
          const status = getStepStatus(step.key);
          const extra = getStepExtra(step.key);
          return (
            <div key={step.key} data-testid={`progress-step-${step.key}`}
              className={`flex items-start gap-3 py-1.5 px-2 rounded-sm transition-colors ${
                status === "running" ? "bg-blue-50 border border-blue-200" :
                status === "waiting" ? "bg-amber-50 border border-amber-300" :
                status === "error" ? "bg-red-50 border border-red-200" :
                status === "completed" ? "bg-white" : "opacity-40"
              }`}
            >
              {/* Icon */}
              <div className="mt-0.5 flex-shrink-0">
                {status === "completed" && <CheckCircle size={16} weight="fill" className="text-emerald-500" />}
                {status === "running" && <CircleNotch size={16} className="text-blue-500 animate-spin" />}
                {status === "waiting" && <Keyboard size={16} weight="fill" className="text-amber-500" />}
                {status === "error" && <Warning size={16} weight="fill" className="text-red-500" />}
                {status === "pending" && <div className="w-4 h-4 rounded-full border-2 border-zinc-300" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-xs ${status === "error" ? "text-red-700 font-semibold" : status === "running" ? "text-blue-700 font-semibold" : status === "completed" ? "text-zinc-700" : "text-zinc-400"}`}>
                  {step.label}
                </p>
                {extra?.note && (
                  <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">{extra.note}</p>
                )}
                {extra?.rows_scraped !== undefined && (
                  <p className="text-[10px] text-emerald-600 font-mono mt-0.5">{extra.rows_scraped} baris data ditemukan</p>
                )}
                {extra?.parsed_count !== undefined && (
                  <p className="text-[10px] text-emerald-600 font-mono mt-0.5">{extra.parsed_count} transaksi di-parse</p>
                )}
                {extra?.processed !== undefined && (
                  <p className="text-[10px] text-blue-600 font-mono mt-0.5">Record {extra.processed}/{extra.total}</p>
                )}
                {extra?.captcha_text && (
                  <p className="text-[10px] text-zinc-500 font-mono mt-0.5">CAPTCHA: {extra.captcha_text}</p>
                )}
                {extra?.verified !== undefined && (
                  <p className="text-[10px] text-emerald-600 font-mono mt-0.5">PG Verified: {extra.verified} | Mismatch: {extra.mismatched || 0} | Not Found: {extra.not_found || 0}</p>
                )}
              </div>

              {/* Est time */}
              <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">~{step.est_seconds}d</span>
            </div>
          );
        })}
      </div>

      {/* Manual CAPTCHA Input Section */}
      {progress.status === "waiting_captcha" && progress.waiting_captcha && (
        <div data-testid="captcha-input-section" className="border-t-2 border-amber-400 bg-amber-50 p-4 space-y-4">
          <div className="flex items-start gap-3">
            <Keyboard size={22} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900">
                Input CAPTCHA Manual {progress.captcha_source?.includes("pg_") ? "(Payment Gateway)" : "(Panel Login)"}
              </p>
              <p className="text-xs text-amber-700 mt-0.5">AI gagal membaca CAPTCHA. Ketik teks CAPTCHA yang terlihat di gambar di bawah.</p>
            </div>
          </div>

          {/* CAPTCHA Image */}
          <div className="flex justify-center">
            <div className="border-2 border-amber-300 rounded-sm overflow-hidden bg-white p-2">
              <img
                data-testid="captcha-image"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/captcha-image?t=${Date.now()}`}
                alt="CAPTCHA"
                className="max-h-[200px] object-contain cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setZoomOpen(true)}
              />
            </div>
            <p className="text-[10px] text-amber-600 mt-1 text-center">Klik gambar untuk memperbesar</p>
          </div>

          {/* Input + Submit */}
          <div className="flex items-center gap-2 max-w-md mx-auto">
            <Input
              ref={captchaInputRef}
              data-testid="captcha-text-input"
              value={captchaInput}
              onChange={(e) => setCaptchaInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") handleCaptchaSubmit(); }}
              placeholder="Ketik CAPTCHA di sini..."
              className="rounded-sm border-amber-300 font-mono text-lg tracking-[0.3em] text-center uppercase h-11"
              maxLength={8}
              autoFocus
            />
            <Button
              data-testid="captcha-submit-btn"
              onClick={handleCaptchaSubmit}
              disabled={captchaSubmitting || !captchaInput.trim()}
              className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm h-11 px-4 gap-2"
            >
              <PaperPlaneTilt size={16} />
              {captchaSubmitting ? "..." : "Kirim"}
            </Button>
          </div>

          {captchaError && (
            <p className="text-xs text-red-600 text-center">{captchaError}</p>
          )}

          {/* Full page screenshot for context */}
          <details className="mt-2">
            <summary className="text-[10px] text-amber-700 cursor-pointer font-semibold uppercase tracking-wider text-center">
              Lihat screenshot halaman login (untuk konteks)
            </summary>
            <div className="mt-2 border border-amber-200 rounded-sm overflow-hidden">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/captcha-page-screenshot?t=${Date.now()}`}
                alt="Login Page"
                className="w-full max-h-[300px] object-contain bg-zinc-900"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          </details>

          <p className="text-[10px] text-amber-600 text-center font-mono">Timeout dalam 5 menit. CAPTCHA akan refresh otomatis jika salah.</p>
        </div>
      )}

      {/* Error detail section */}
      {progress.status === "error" && progress.error && (
        <div data-testid="progress-error-detail" className="border-t border-red-200 bg-red-50 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <Warning size={18} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Error: {progress.error}</p>
              {progress.error_detail && (
                <details className="mt-2">
                  <summary className="text-[10px] text-red-600 cursor-pointer font-semibold uppercase tracking-wider">Detail Error (klik untuk buka)</summary>
                  <pre className="mt-1 text-[10px] text-red-700 bg-red-100 rounded-sm p-2 overflow-x-auto whitespace-pre-wrap max-h-32 font-mono">{progress.error_detail}</pre>
                </details>
              )}
            </div>
          </div>

          {/* Screenshot button */}
          {screenshotUrl && (
            <div>
              <button
                data-testid="show-error-screenshot-btn"
                onClick={() => setShowScreenshot(!showScreenshot)}
                className="flex items-center gap-2 text-xs font-semibold text-red-700 hover:text-red-900 transition-colors"
              >
                <Image size={14} />
                {showScreenshot ? "Sembunyikan Screenshot" : "Lihat Screenshot Saat Error"}
              </button>
              {showScreenshot && (
                <div className="mt-2 border border-red-200 rounded-sm overflow-hidden">
                  <img
                    data-testid="error-screenshot-img"
                    src={screenshotUrl}
                    alt="Error Screenshot"
                    className="w-full max-h-[400px] object-contain bg-zinc-900"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Completed summary */}
      {progress.status === "completed" && (
        <div className="border-t border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2">
          <CheckCircle size={18} weight="fill" className="text-emerald-500" />
          <p className="text-sm text-emerald-800 font-semibold">Rekonsiliasi selesai dalam {formatTime(elapsed)}</p>
        </div>
      )}

      {/* Captcha Zoom Modal */}
      {zoomOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomOpen(false)}>
          <div className="relative max-w-[95vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setZoomOpen(false)}
              className="absolute -top-3 -right-3 bg-white text-zinc-800 rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-red-50 z-10 text-lg font-bold">×</button>
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/captcha-image?t=${Date.now()}`}
              alt="CAPTCHA Zoom"
              className="max-w-[95vw] max-h-[85vh] object-contain rounded-sm border-2 border-white shadow-2xl"
            />
            <p className="text-center text-white/70 text-xs mt-2">Klik di luar gambar atau × untuk menutup</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsLimit] = useState(100);
  const [retryingId, setRetryingId] = useState(null);

  // PG Check (Stage 2) — Per-PG cards
  const [pgPanels, setPgPanels] = useState([]);
  const [pgPanelsLoading, setPgPanelsLoading] = useState(false);
  const [pgRunning, setPgRunning] = useState(null); // pg_name that's running
  const [showPgProgress, setShowPgProgress] = useState(false);
  const [pgProgress, setPgProgress] = useState(null);
  const pgPollRef = useRef(null);

  // PG Captcha (for Stage 2)
  const [pgCaptchaInput, setPgCaptchaInput] = useState("");
  const [captchaZoom, setCaptchaZoom] = useState(false); // unused, kept for compat
  const handlePgCaptchaSubmit = async () => {
    if (!pgCaptchaInput.trim()) return;
    try {
      await API.post(`/jobs/${id}/captcha-input`, { captcha_text: pgCaptchaInput.trim() });
      setPgCaptchaInput("");
    } catch (e) { console.error("PG captcha submit error:", e); }
  };

  // Saldo Internal
  const [saldoData, setSaldoData] = useState(null);
  const [saldoLoading, setSaldoLoading] = useState(false);
  const [showSaldo, setShowSaldo] = useState(false);

  // Cross-Midnight Resolutions (Phase 4)
  const [cmData, setCmData] = useState(null);
  const [cmLoading, setCmLoading] = useState(false);
  const [showCm, setShowCm] = useState(false);

  const loadCrossMidnight = useCallback(async () => {
    if (!id) return;
    setCmLoading(true);
    try {
      const { data } = await API.get(`/jobs/${id}/cross-midnight`);
      setCmData(data);
    } catch (e) {
      console.error("CM load error:", e);
    } finally {
      setCmLoading(false);
    }
  }, [id]);

  const undoCrossMidnight = async (resolutionId) => {
    if (!window.confirm("Undo resolusi cross-midnight ini? Panel record & sheet outflow akan kembali ke status semula.")) return;
    try {
      await API.post(`/jobs/${id}/cross-midnight/${resolutionId}/undo`);
      await loadCrossMidnight();
    } catch (e) {
      alert("Gagal undo: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if (id && job?.status && !["running", "pending"].includes(job.status)) {
      loadCrossMidnight();
    }
  }, [id, job?.status, loadCrossMidnight]);

  // PG Orphan Withdrawals
  const [orphanData, setOrphanData] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  const [showOrphans, setShowOrphans] = useState(false);

  // PG Anomalies
  const [anomalyData, setAnomalyData] = useState(null);
  const [anomalyLoading, setAnomalyLoading] = useState(false);

  // Jual Pulsa
  const [pulsaData, setPulsaData] = useState(null);
  const [pulsaLoading, setPulsaLoading] = useState(false);

  // Suntik Dana TMP
  const [tmpData, setTmpData] = useState(null);
  const [tmpLoading, setTmpLoading] = useState(false);

  // AI Analysis
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  // Deep Analysis — readiness gate + verdicts
  const [deepReady, setDeepReady] = useState(null); // { ready, checklist, missing_required, missing_optional }
  const [deepAnalysis, setDeepAnalysis] = useState(null);
  const [deepLoading, setDeepLoading] = useState(false);
  const [haystackCache, setHaystackCache] = useState(null); // { exists, cached_at, age_seconds, is_fresh }
  const [deepPage, setDeepPage] = useState(0);
  const DEEP_PAGE_SIZE = 10;
  const [deepFilter, setDeepFilter] = useState("all"); // all | match | review | no_match | applied
  const [cmPage, setCmPage] = useState(0);
  const CM_PAGE_SIZE = 20;

  const loadAiAnalysis = async () => {
    try {
      const { data } = await API.get(`/jobs/${id}/ai-analysis`);
      if (data.exists) setAiAnalysis(data);
    } catch {}
  };

  const loadDeepReadiness = async () => {
    try {
      const { data } = await API.get(`/jobs/${id}/deep-readiness`);
      setDeepReady(data);
    } catch {}
  };

  const loadDeepAnalysis = async () => {
    try {
      const { data } = await API.get(`/jobs/${id}/deep-analysis`);
      if (data.exists) setDeepAnalysis(data);
    } catch {}
  };

  const loadHaystackCache = async () => {
    try {
      const { data } = await API.get(`/jobs/${id}/deep-haystack-cache`);
      setHaystackCache(data);
    } catch {}
  };

  const triggerAiAnalysis = async () => {
    setAiLoading(true);
    try {
      const { data } = await API.post(`/jobs/${id}/ai-analyze`);
      setAiAnalysis({ ...data, exists: true });
    } catch (e) { alert("Gagal: " + (e.response?.data?.detail || e.message)); }
    finally { setAiLoading(false); }
  };

  const triggerDeepAnalysis = async (forceRefresh = false) => {
    setDeepLoading(true);
    try {
      const qs = forceRefresh ? "?force_refresh=true" : "";
      const { data } = await API.post(`/jobs/${id}/deep-analyze${qs}`);
      setDeepAnalysis({ ...data, exists: true });
      loadHaystackCache();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      if (d.missing_required?.length) {
        alert("Belum siap:\n- " + d.missing_required.join("\n- "));
      } else {
        alert("Gagal Deep Analysis: " + (typeof d === "string" ? d : (d.error || e.message)));
      }
    }
    finally { setDeepLoading(false); }
  };

  const loadSaldo = async (forceRefresh = false) => {
    setSaldoLoading(true);
    try {
      const { data } = await API.get(`/records/saldo-internal/${id}${forceRefresh ? '?refresh=true' : ''}`);
      setSaldoData(data);
      setShowSaldo(true);
    } catch (e) { console.error(e); }
    finally { setSaldoLoading(false); }
  };

  const loadOrphans = async () => {
    setOrphanLoading(true);
    try {
      const { data } = await API.get(`/jobs/${id}/pg-orphans`);
      setOrphanData(data);
      setShowOrphans(true);
    } catch (e) { console.error(e); }
    finally { setOrphanLoading(false); }
  };

  const loadAnomalies = async () => {
    setAnomalyLoading(true);
    try {
      const { data } = await API.get(`/jobs/${id}/pg-anomalies`);
      setAnomalyData(data);
    } catch (e) { console.error(e); }
    finally { setAnomalyLoading(false); }
  };

  const loadPulsa = async (forceRefresh = false) => {
    setPulsaLoading(true);
    try {
      const { data } = await API.get(`/records/jual-pulsa/${id}${forceRefresh ? '?refresh=true' : ''}`);
      setPulsaData(data);
    } catch (e) { console.error(e); }
    finally { setPulsaLoading(false); }
  };

  const verifyPulsa = async (key, verified) => {
    try {
      await API.post("/records/jual-pulsa/verify", { job_id: id, keys: [key], verified });
      setPulsaData(prev => {
        if (!prev) return prev;
        const records = prev.records.map(r => r.verify_key === key ? { ...r, verified } : r);
        const total_verified = records.filter(r => r.verified).length;
        return { ...prev, records, summary: { ...prev.summary, total_verified } };
      });
    } catch (e) { console.error(e); }
  };

  const verifyAllPulsa = async () => {
    if (!pulsaData?.records?.length) return;
    const unverifiedKeys = pulsaData.records.filter(r => !r.verified).map(r => r.verify_key);
    if (!unverifiedKeys.length) return;
    try {
      await API.post("/records/jual-pulsa/verify", { job_id: id, keys: unverifiedKeys, verified: true });
      setPulsaData(prev => {
        if (!prev) return prev;
        const records = prev.records.map(r => ({ ...r, verified: true }));
        return { ...prev, records, summary: { ...prev.summary, total_verified: records.length } };
      });
    } catch (e) { console.error(e); }
  };

  const loadTmpMatch = async (forceRefresh = false) => {
    setTmpLoading(true);
    try {
      const { data } = await API.get(`/records/suntik-dana-tmp/${id}${forceRefresh ? '?refresh=true' : ''}`);
      setTmpData(data);
    } catch (e) { console.error(e); }
    finally { setTmpLoading(false); }
  };

  const solveTmpItem = async (key, solved) => {
    try {
      await API.post("/records/suntik-dana-tmp/solve", { job_id: id, keys: [key], solved });
      setTmpData(prev => {
        if (!prev) return prev;
        const unmatched = prev.unmatched.map(u => u.solve_key === key ? { ...u, solved } : u);
        const total_solved = unmatched.filter(u => u.solved).length;
        return { ...prev, unmatched, summary: { ...prev.summary, total_solved } };
      });
    } catch (e) { console.error(e); }
  };

  const bulkSolveTmp = async () => {
    if (!tmpData?.unmatched?.length) return;
    const unsolvedKeys = tmpData.unmatched.filter(u => !u.solved).map(u => u.solve_key);
    if (!unsolvedKeys.length) return;
    try {
      await API.post("/records/suntik-dana-tmp/solve", { job_id: id, keys: unsolvedKeys, solved: true });
      setTmpData(prev => {
        if (!prev) return prev;
        const unmatched = prev.unmatched.map(u => ({ ...u, solved: true }));
        return { ...prev, unmatched, summary: { ...prev.summary, total_solved: unmatched.length } };
      });
    } catch (e) { console.error(e); }
  };

  // PG Check handlers
  const loadPgPanels = useCallback(async () => {
    if (!id) return;
    setPgPanelsLoading(true);
    try {
      const { data } = await API.get(`/jobs/${id}/pg-panels`);
      setPgPanels(data.panels || []);
    } catch (e) { console.error("PG panels load error:", e); }
    finally { setPgPanelsLoading(false); }
  }, [id]);

  const handleRunPgCheck = async (pgName) => {
    setPgRunning(pgName);
    try {
      await API.post(`/jobs/${id}/run-pg-check`, { pg_name: pgName });
      setShowPgProgress(true);
      startPgPolling();
      setJob(prev => prev ? { ...prev, pg_check_status: "running" } : prev);
    } catch (e) { alert(e.response?.data?.detail || "Gagal memulai PG check"); }
    finally { setPgRunning(null); }
  };

  const startPgPolling = useCallback(() => {
    if (pgPollRef.current) clearInterval(pgPollRef.current);
    pgPollRef.current = setInterval(async () => {
      try {
        const { data } = await API.get(`/jobs/${id}/pg-progress`);
        setPgProgress(data);
        if (data.status === "completed" || data.status === "error") {
          clearInterval(pgPollRef.current);
          pgPollRef.current = null;
          loadJob();
        }
      } catch (e) { console.error("PG poll error:", e); }
    }, 2500);
  }, [id]);

  useEffect(() => {
    return () => { if (pgPollRef.current) clearInterval(pgPollRef.current); };
  }, []);

  // Auto-start PG polling if PG check is already running
  useEffect(() => {
    if (job?.pg_check_status === "running" || job?.pg_check_status === "waiting_captcha" || job?.pg_check_status === "failed") {
      setShowPgProgress(true);
      if (job?.pg_check_status === "running" || job?.pg_check_status === "waiting_captcha") {
        startPgPolling();
      }
    }
    // Load PG panels when job is done (Stage 1 completed)
    if (job?.pg_check_available && !["running", "pending"].includes(job?.status)) {
      loadPgPanels();
    }
  }, [job?.pg_check_status, job?.pg_check_available, job?.status, startPgPolling, loadPgPanels]);

  // Load AI analysis if exists
  useEffect(() => {
    if (job && !["running", "pending"].includes(job.status)) {
      loadAiAnalysis();
      loadDeepReadiness();
      loadDeepAnalysis();
      loadHaystackCache();
    }
  }, [job?.id, job?.status]);

  // Auto-refresh haystack cache info every 5s while deep analysis is shown
  useEffect(() => {
    if (!deepAnalysis?.exists) return;
    const interval = setInterval(() => {
      loadHaystackCache();
    }, 5000);
    return () => clearInterval(interval);
  }, [deepAnalysis?.exists, id]);

  // Reset pagination when data refreshes
  useEffect(() => { setDeepPage(0); }, [deepAnalysis?.analyzed_at]);
  useEffect(() => { setDeepPage(0); }, [deepFilter]);
  useEffect(() => { setCmPage(0); }, [cmData?.resolutions?.length]);

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterClassification, setFilterClassification] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterStatusPanel, setFilterStatusPanel] = useState("all");
  const [filterFundMethod, setFilterFundMethod] = useState("all");
  const [filterApprovedBy, setFilterApprovedBy] = useState("all");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");

  // Sort
  const [sortField, setSortField] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => { loadJob(); }, [id, recordsPage, filterType, filterClassification, filterStatus, filterStatusPanel, filterFundMethod, filterApprovedBy, filterDateFrom, filterDateTo, sortField, sortDir]);

  // Debounce search
  const searchTimerRef = useRef(null);
  const [searchApplied, setSearchApplied] = useState("");
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearchApplied(filterSearch);
      setRecordsPage(1);
    }, 500);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [filterSearch]);
  useEffect(() => { if (searchApplied !== undefined) loadJob(); }, [searchApplied]);

  const loadJob = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        records_page: recordsPage,
        records_limit: recordsLimit,
        filter_type: filterType,
        filter_classification: filterClassification,
        filter_status: filterStatus,
        filter_status_panel: filterStatusPanel,
        filter_fund_method: filterFundMethod,
        filter_approved_by: filterApprovedBy,
        filter_search: searchApplied || "",
        filter_date_from: filterDateFrom || "",
        filter_date_to: filterDateTo || "",
        sort_field: sortField,
        sort_dir: sortDir,
      });
      const { data } = await API.get(`/jobs/${id}?${params.toString()}`);
      setJob(data);
      if (data.status === "running" || data.status === "failed") setShowProgress(true);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [id, recordsPage, recordsLimit, filterType, filterClassification, filterStatus, filterStatusPanel, filterFundMethod, filterApprovedBy, searchApplied, filterDateFrom, filterDateTo, sortField, sortDir]);

  const handleRun = async () => {
    setRunning(true);
    try {
      await API.post(`/jobs/${id}/run`);
      setShowProgress(true);
      setJob(prev => prev ? { ...prev, status: "running" } : prev);
    } catch (e) { alert(e.response?.data?.detail || "Gagal menjalankan job"); }
    finally { setRunning(false); }
  };

  const handleRetryJob = async () => {
    setRetrying(true);
    try {
      await API.post(`/jobs/${id}/retry`);
      setShowProgress(true);
      setJob(prev => prev ? { ...prev, status: "running", total_records: 0, matched_count: 0, pg_count: 0, unmatched_count: 0, review_count: 0 } : prev);
    } catch (e) { alert(e.response?.data?.detail || "Gagal retry job"); }
    finally { setRetrying(false); }
  };

  const handleForceRetry = async () => {
    if (!window.confirm("Force Retry akan mereset job yang stuck dan menjalankan ulang dari awal. Lanjutkan?")) return;
    setRetrying(true);
    try {
      await API.post(`/jobs/${id}/force-retry`);
      setShowProgress(true);
      setJob(prev => prev ? { ...prev, status: "running", total_records: 0, matched_count: 0, pg_count: 0, unmatched_count: 0, review_count: 0 } : prev);
    } catch (e) { alert(e.response?.data?.detail || "Gagal force retry"); }
    finally { setRetrying(false); }
  };

  const handleRetryRecord = async (recordId) => {
    setRetryingId(recordId);
    try {
      await API.post(`/records/${recordId}/retry`);
      await loadJob();
    } catch (e) { alert("Retry gagal"); }
    finally { setRetryingId(null); }
  };

  const [bulkRetrying, setBulkRetrying] = useState(false);
  const handleBulkRetryByFilter = async (filterType) => {
    const total = job?.records_total || 0;
    if (!total) return;
    const label = filterType === "duplicate" ? "duplikat" : "not found";
    if (!window.confirm(`Retry semua ${total} record ${label}? Operator pastikan sheet sudah dibetulkan terlebih dahulu.`)) return;
    setBulkRetrying(true);
    try {
      const { data } = await API.post(`/records/bulk-retry-by-filter`, {
        job_id: id, filter_type: filterType,
      });
      alert(`Bulk retry selesai\n\nTotal: ${data.total}\nSuccess: ${data.success}\nFailed: ${data.failed}`);
      await loadJob();
    } catch (e) {
      alert("Bulk retry gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBulkRetrying(false);
    }
  };

  const handleProgressComplete = useCallback(() => {
    loadJob();
    // Don't auto-close progress panel — let user dismiss it
  }, [loadJob]);

  const handleDismissProgress = useCallback(() => {
    setShowProgress(false);
    loadJob();
  }, [loadJob]);

  // Filter options from server (all unique values across entire job)
  const filterOptions = job?.filter_options || {};

  // Records are already filtered & sorted by server
  const records = job?.records || [];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setRecordsPage(1);
  };

  const resetFilters = () => {
    setFilterType("all"); setFilterClassification("all"); setFilterStatus("all");
    setFilterStatusPanel("all"); setFilterFundMethod("all"); setFilterApprovedBy("all");
    setFilterSearch(""); setSearchApplied("");
    setFilterDateFrom(""); setFilterDateTo("");
    setRecordsPage(1);
  };

  const activeFilterCount = [filterType, filterClassification, filterStatus, filterStatusPanel, filterFundMethod, filterApprovedBy].filter(f => f !== "all").length + (filterSearch ? 1 : 0) + (filterDateFrom ? 1 : 0) + (filterDateTo ? 1 : 0);

  // When filter changes, reset to page 1
  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setRecordsPage(1);
  };

  const handleExport = () => {
    const csvRows = [
      ["Transaction ID", "Date", "Username", "Account Name", "Type", "Classification", "Amount", "Fund Method", "Bank Details", "Status Panel", "Approved By", "Recon Status", "Score", "Sheet Tab", "Row"].join(","),
      ...records.map((r) =>
        [r.transaction_id, r.transaction_date, r.username, `"${(r.account_name||"").replace(/"/g,'')}"`, r.transaction_type, r.classification, r.amount, `"${r.fund_method||""}"`, `"${(r.bank_details||"").replace(/"/g,'')}"`, r.status_panel, r.confirmed_by, r.final_status, r.confidence_score, r.matched_sheet_tab || "", r.matched_row_reference || ""].join(",")
      ),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rekonsiliasi-${job?.date_from}-${job?.date_to}.csv`;
    a.click();
  };

  const stats = job?.filtered_stats || {};

  if (loading) return <div className="p-8 text-sm text-zinc-400 font-mono">Loading...</div>;
  if (!job) return <div className="p-8 text-sm text-red-500">Job tidak ditemukan</div>;

  const isRunningOrFailed = ["running", "failed"].includes(job.status);

  return (
    <div data-testid="job-detail-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/jobs")} className="text-zinc-400 hover:text-zinc-900 transition-colors"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Detail Job</h1>
          <p className="text-xs font-mono text-zinc-400 mt-0.5">
            {job.site_name && <span className="text-zinc-600 font-semibold">{job.site_name}</span>}
            {job.site_name && " | "}
            {job.id} | {job.date_from} ~ {job.date_to} | {job.record_type}
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-btn" onClick={handleExport} variant="outline" className="rounded-sm gap-2 border-zinc-200"><DownloadSimple size={16} /> Export ({job?.records_total || 0})</Button>
          {job.status !== "running" && job.status !== "pending" && (
            <Button data-testid="retry-job-btn" onClick={handleRetryJob} disabled={retrying} variant="outline"
              className="rounded-sm gap-2 border-zinc-300 text-zinc-700 hover:bg-zinc-100">
              <ArrowClockwise size={16} /> {retrying ? "Memulai..." : "Retry Job"}
            </Button>
          )}
          {job.status === "running" && (
            <Button data-testid="force-retry-btn" onClick={handleForceRetry} disabled={retrying} variant="outline"
              className="rounded-sm gap-2 border-red-300 text-red-700 hover:bg-red-50">
              <ArrowClockwise size={16} /> {retrying ? "Memulai..." : "Force Retry"}
            </Button>
          )}
          {(job.status === "pending" || job.status === "failed") && (
            <Button data-testid="run-job-btn" onClick={handleRun} disabled={running} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2">
              <Play size={16} /> {running ? "Memulai..." : job.status === "failed" ? "Jalankan Ulang" : "Jalankan"}
            </Button>
          )}
        </div>
      </div>

      {/* Progress Panel - shown when running, failed, or recently completed */}
      {showProgress && (
        <JobProgressPanel jobId={id} onComplete={handleProgressComplete} onDismiss={handleDismissProgress} />
      )}

      {/* PG Gateway Cards — separate card per detected PG */}
      {job.pg_check_available && !["running", "pending"].includes(job.status) && (
        <div data-testid="pg-cards-section" className="space-y-3">
          {pgPanelsLoading ? (
            <div className="flex items-center gap-2 text-xs text-zinc-400 py-4">
              <CircleNotch size={14} className="animate-spin" />
              <span>Mendeteksi Payment Gateway...</span>
            </div>
          ) : pgPanels.length === 0 ? (
            <div className="text-xs text-zinc-400 py-2">Tidak ada Payment Gateway terdeteksi dari data panel.</div>
          ) : (
            pgPanels.map((pg) => {
              const pgRes = job.pg_results?.[pg.name] || {};
              const status = pgRes.status || pg.status || "pending";
              const isRunning = status === "running" || (job.pg_check_status === "running" && pgRunning === pg.name);
              const isCompleted = status === "completed";
              const isFailed = status === "failed";
              const isPending = !isCompleted && !isFailed && !isRunning;

              const borderColor = isCompleted ? "border-emerald-300" :
                isRunning ? "border-blue-300" : isFailed ? "border-red-300" : "border-zinc-200";
              const headerBg = isCompleted ? "bg-emerald-600" :
                isRunning ? "bg-blue-600" : isFailed ? "bg-red-600" : "bg-zinc-800";

              return (
                <div key={pg.name} data-testid={`pg-card-${pg.name}`} className={`border-2 rounded-sm overflow-hidden ${borderColor}`}>
                  <div className={`px-4 py-3 flex items-center justify-between ${headerBg} text-white`}>
                    <div className="flex items-center gap-3">
                      {isRunning ? <CircleNotch size={18} className="animate-spin" /> :
                       isCompleted ? <CheckCircle size={18} weight="fill" /> :
                       isFailed ? <Warning size={18} weight="fill" /> :
                       <div className="w-[18px] h-[18px] rounded-full border-2 border-white/40" />}
                      <div>
                        <p className="text-sm font-bold">{pg.display_name}</p>
                        <p className="text-[11px] opacity-80">
                          {isCompleted ? (
                            `Verified: ${pgRes.verified || 0} | Mismatch: ${pgRes.mismatch || 0} | Not Found: ${pgRes.not_found || 0}${pgRes.pending ? ` | Pending PG: ${pgRes.pending}` : ''}`
                          ) : isRunning ? (
                            "Sedang memverifikasi..."
                          ) : isFailed ? (
                            `Error: ${pgRes.error || "Unknown"}`
                          ) : (
                            `${pg.deposit_count} deposit, ${pg.withdraw_count} withdraw terdeteksi`
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!pg.has_scraper && isPending && (
                        <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-sm">No Scraper</span>
                      )}
                      {pg.has_scraper && (isPending || isFailed) && (
                        <Button
                          data-testid={`run-pg-${pg.name}-btn`}
                          onClick={() => handleRunPgCheck(pg.name)}
                          disabled={pgRunning === pg.name || job.pg_check_status === "running"}
                          className="bg-white text-zinc-900 hover:bg-zinc-100 rounded-sm gap-1.5 text-xs h-7 px-3"
                        >
                          <Play size={12} weight="fill" />
                          {pgRunning === pg.name ? "Memulai..." : isFailed ? "Retry" : "Verifikasi"}
                        </Button>
                      )}
                      {pg.has_scraper && isCompleted && (
                        <Button
                          data-testid={`retry-pg-${pg.name}-btn`}
                          onClick={() => handleRunPgCheck(pg.name)}
                          disabled={pgRunning === pg.name}
                          variant="outline"
                          className="border-white/30 text-white hover:bg-white/10 rounded-sm gap-1.5 text-xs h-7 px-3"
                        >
                          <ArrowClockwise size={12} />
                          Ulang
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Per-PG stats row */}
                  {(isCompleted || pg.total_count > 0) && (
                    <div className="px-4 py-2 bg-zinc-50 border-t flex items-center gap-x-5 gap-y-1 text-[11px] font-mono flex-wrap">
                      <span className="text-zinc-500">Panel: <strong className="text-zinc-800">{pg.total_count}</strong></span>
                      {pg.deposit_count > 0 && (
                        <span className="text-blue-600">
                          Deposit: <strong>{pg.deposit_count}</strong>
                          <span className="text-emerald-600 ml-1">({pg.deposit_confirmed} confirmed</span>
                          {pg.deposit_failed > 0 && <span className="text-red-500">, {pg.deposit_failed} failed</span>}
                          <span className="text-emerald-600">)</span>
                        </span>
                      )}
                      {pg.withdraw_count > 0 && (
                        <span className="text-orange-600">
                          Withdraw: <strong>{pg.withdraw_count}</strong>
                          <span className="text-emerald-600 ml-1">({pg.withdraw_confirmed} confirmed</span>
                          {pg.withdraw_failed > 0 && <span className="text-red-500">, {pg.withdraw_failed} failed</span>}
                          <span className="text-emerald-600">)</span>
                        </span>
                      )}
                      {isCompleted && (
                        <>
                          <span className="text-zinc-300">|</span>
                          <span className="text-emerald-600">Verified: <strong>{pgRes.verified || 0}</strong></span>
                          {(pgRes.mismatch || 0) > 0 && <span className="text-red-600">Mismatch: <strong>{pgRes.mismatch}</strong></span>}
                          <span className="text-amber-600">Not Found: <strong>{pgRes.not_found || 0}</strong></span>
                          {(pgRes.pending || 0) > 0 && <span className="text-violet-600">Pending PG: <strong>{pgRes.pending}</strong></span>}
                          {(pgRes.anomaly || 0) > 0 && <span className="text-orange-600">Anomali: <strong>{pgRes.anomaly}</strong>{pgRes.adj_linked > 0 && <span className="text-emerald-600 ml-1">({pgRes.adj_linked} linked)</span>}</span>}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* PG Progress Steps — shared for whichever PG is running */}
          {showPgProgress && (job.pg_check_status === "running" || job.pg_check_status === "waiting_captcha" || job.pg_check_status === "failed") && (
            <div className="border border-zinc-200 rounded-sm p-4 space-y-1 bg-white max-h-[500px] overflow-y-auto">
              {(!pgProgress || pgProgress.status === "idle") ? (
                <div className="flex items-center gap-3 py-3 px-2">
                  <CircleNotch size={16} className="text-blue-500 animate-spin" />
                  <p className="text-xs text-blue-700 font-semibold">Memulai PG verification...</p>
                </div>
              ) : (
                <>
                  {(pgProgress.all_steps || []).map((step) => {
                    const stepData = pgProgress.steps?.filter(s => s.key === step.key) || [];
                    const latest = stepData[stepData.length - 1];
                    const stepStatus = latest?.status || "pending";
                    const completedKeys = pgProgress.steps?.filter(s => s.status === "completed").map(s => s.key) || [];
                    const isCompleted = completedKeys.includes(step.key);
                    const isCurrent = pgProgress.current_step === step.key;

                    return (
                      <div key={step.key} className={`flex items-start gap-3 py-1.5 px-2 rounded-sm transition-colors ${
                        isCompleted ? "bg-white" :
                        isCurrent && stepStatus === "waiting_captcha" ? "bg-amber-50 border border-amber-300" :
                        isCurrent ? "bg-blue-50 border border-blue-200" :
                        stepStatus === "error" ? "bg-red-50 border border-red-200" :
                        "opacity-40"
                      }`}>
                        <div className="mt-0.5 flex-shrink-0">
                          {isCompleted && <CheckCircle size={16} weight="fill" className="text-emerald-500" />}
                          {isCurrent && stepStatus !== "error" && stepStatus !== "completed" && stepStatus !== "waiting_captcha" && <CircleNotch size={16} className="text-blue-500 animate-spin" />}
                          {isCurrent && stepStatus === "waiting_captcha" && <Keyboard size={16} weight="fill" className="text-amber-500" />}
                          {stepStatus === "error" && <Warning size={16} weight="fill" className="text-red-500" />}
                          {!isCompleted && !isCurrent && stepStatus !== "error" && <div className="w-4 h-4 rounded-full border-2 border-zinc-300" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs ${stepStatus === "error" ? "text-red-700 font-semibold" : isCompleted ? "text-zinc-700" : isCurrent ? "text-blue-700 font-semibold" : "text-zinc-400"}`}>{step.label}</p>
                          {latest?.error && <p className="text-[10px] text-red-600 mt-0.5 font-mono">{latest.error}</p>}
                          {latest?.note && !latest?.error && <p className="text-[10px] text-zinc-500 mt-0.5 font-mono">{latest.note}</p>}
                          {latest?.verified !== undefined && (
                            <p className="text-[10px] text-emerald-600 font-mono mt-0.5">Verified: {latest.verified} | Mismatch: {latest.mismatched || 0} | Not Found: {latest.not_found || 0}</p>
                          )}
                        </div>
                        <span className="text-[10px] font-mono text-zinc-400 flex-shrink-0">~{step.est_seconds}d</span>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* PG Captcha Input */}
          {(job.pg_check_status === "waiting_captcha" || (pgProgress?.status === "waiting_captcha" && pgProgress?.waiting_captcha)) && (
            <div className="border-2 border-amber-400 bg-amber-50 p-4 space-y-3 rounded-sm">
              <p className="text-sm font-semibold text-amber-900">CAPTCHA PG — Input Manual</p>
              <div className="flex justify-center">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${id}/captcha-image?t=${Date.now()}`}
                  alt="PG CAPTCHA"
                  className="w-full max-w-[500px] object-contain border-2 border-amber-300 rounded-sm bg-zinc-900 p-1"
                />
              </div>
              <div className="flex items-center gap-2 max-w-md mx-auto">
                <Input
                  data-testid="pg-captcha-input"
                  value={pgCaptchaInput}
                  onChange={(e) => setPgCaptchaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handlePgCaptchaSubmit(); }}
                  placeholder="Ketik captcha..."
                  className="rounded-sm border-amber-300 font-mono text-lg tracking-[0.2em] text-center h-10 min-w-[200px]"
                  maxLength={8}
                  autoFocus
                />
                <Button
                  data-testid="pg-captcha-submit"
                  onClick={handlePgCaptchaSubmit}
                  className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm h-10 px-4"
                >
                  Kirim
                </Button>
              </div>
            </div>
          )}

          {/* Overall PG summary when any completed */}
          {(job.pg_verified_count > 0 || job.pg_mismatch_count > 0 || job.pg_notfound_count > 0) && (
            <div className="px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-sm flex items-center gap-4 text-xs font-mono">
              <span className="text-zinc-600 font-semibold">Total PG:</span>
              <span className="text-emerald-700">Verified: <strong>{job.pg_verified_count || 0}</strong></span>
              <span className="text-red-600">Mismatch: <strong>{job.pg_mismatch_count || 0}</strong></span>
              <span className="text-amber-600">Not Found: <strong>{job.pg_notfound_count || 0}</strong></span>
            </div>
          )}
        </div>
      )}

      {/* Job Summary Cards */}
      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-12 gap-2">
        <MC label="Status" value={<StatusBadge status={job.status} />} />
        <MC label="Total" value={job.total_records} mono />
        <MC label="Matched" value={job.matched_count || 0} mono color="text-emerald-600" />
        <MC label="PG" value={job.pg_count || 0} mono color="text-violet-600" />
        <MC label="PG Verified" value={job.pg_verified_count || 0} mono color={job.pg_verified_count > 0 ? "text-emerald-600" : "text-zinc-400"} />
        <MC label="PG Mismatch" value={job.pg_mismatch_count || 0} mono color={job.pg_mismatch_count > 0 ? "text-red-600 font-bold" : "text-zinc-400"} />
        <MC label="Review" value={job.review_count || 0} mono color="text-amber-600" testId="counter-review" />
        <MC label="Not Found" value={job.unmatched_count || 0} mono color="text-red-600" testId="counter-not-found" pulse={(job.unmatched_count || 0) > 0} pulseVariant="red" pulseTitle={`${job.unmatched_count} transaksi panel tidak ketemu di sheet — perlu review`} />
        <MC label="Outflow" value={job.unmatched_outflow_count || 0} mono color={job.unmatched_outflow_count > 0 ? "text-red-700 font-bold" : "text-zinc-400"} testId="counter-outflow" pulse={(job.unmatched_outflow_count || 0) > 0} pulseVariant="red" pulseTitle={`${job.unmatched_outflow_count} dana keluar di sheet tanpa pasangan panel — investigasi segera`} />
        <MC label="Split" value={job.split_count || 0} mono color="text-cyan-600" testId="counter-split" pulse={(job.split_count || 0) > 0} pulseVariant="amber" pulseTitle={`${job.split_count} transaksi terdeteksi split — verifikasi sheet row`} />
        <MC label="Duplikat" value={job.duplicate_count || 0} mono color={job.duplicate_count > 0 ? "text-amber-500 font-bold" : "text-zinc-400"} testId="counter-duplikat" pulse={(job.duplicate_count || 0) > 0} pulseVariant="amber" pulseTitle={`${job.duplicate_count} duplikat baris terdeteksi — cek sheet apakah operator entry 2x`} />
        <MC label="Bonus" value={job.bonus_count || 0} mono color="text-cyan-500" />
        <MC label="Matched AI" value={job.matched_ai_count || 0} mono color={job.matched_ai_count > 0 ? "text-violet-700 font-bold" : "text-zinc-400"} testId="counter-matched-ai" />
        <MC label="Cross-Midnight" value={job.cross_midnight_count || 0} mono color={job.cross_midnight_count > 0 ? "text-indigo-600 font-bold" : "text-zinc-400"} testId="counter-cross-midnight" />
        <MC label="Gantung Verified" value={job.gantung_done_count || 0} mono color={job.gantung_done_count > 0 ? "text-emerald-600 font-bold" : "text-zinc-400"} testId="counter-gantung-done" pulseTitle="GANTUNG VERIFIED — DONE dengan bukti Adjustment Depo nyata di panel records" />
        <MC label="Gantung Unverified" value={job.gantung_done_unverified_count || 0} mono color={job.gantung_done_unverified_count > 0 ? "text-amber-700 font-bold" : "text-zinc-400"} testId="counter-gantung-unverified" pulse={(job.gantung_done_unverified_count || 0) > 0} pulseTitle={`${job.gantung_done_unverified_count} record GANTUNG DONE tanpa bukti adjustment di panel — butuh manual confirm via livechat / dispute log`} />
        <MC label="Gantung Open" value={job.gantung_open_count || 0} mono color={job.gantung_open_count > 0 ? "text-orange-600 font-bold" : "text-zinc-400"} testId="counter-gantung-open" pulse={(job.gantung_open_count || 0) > 10} pulseTitle={`${job.gantung_open_count} kasus GANTUNG masih open — perlu follow-up agar member tidak menunggu lama`} />
        <MC label="Site" value={job.site_name || "-"} />
        <MC label="Tipe" value={job.record_type || "all"} />
        <MC label="Oleh" value={job.triggered_by?.name || "-"} />
        <MC label="Tanggal" value={`${job.date_from || ""} ~ ${job.date_to || ""}`} mono />
      </div>

      {/* AI Analysis Section — only show if job has actual data */}
      {!["running", "pending"].includes(job.status) && job.total_records > 0 && (
        <div data-testid="ai-analysis-section" className="border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50/50">
            <div className="flex items-center gap-2">
              <Lightning size={16} className="text-violet-600" />
              <h3 className="text-sm font-semibold text-zinc-700">AI Analysis</h3>
              {aiAnalysis?.risk_level && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${
                  aiAnalysis.risk_level === "RENDAH" ? "bg-emerald-50 text-emerald-700" :
                  aiAnalysis.risk_level === "SEDANG" ? "bg-amber-50 text-amber-700" :
                  aiAnalysis.risk_level === "TINGGI" ? "bg-orange-50 text-orange-700" :
                  "bg-red-50 text-red-700"
                }`}>
                  Risk: {aiAnalysis.risk_score}/100 ({aiAnalysis.risk_level})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid="ai-analyze-btn" variant="outline" onClick={triggerAiAnalysis} disabled={aiLoading}
                className="rounded-sm text-xs h-7 gap-1.5">
                {aiLoading ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} />}
                {aiLoading ? "Menganalisis..." : aiAnalysis ? "Analisis Ulang" : "Analisis AI"}
              </Button>
              {(() => {
                const ready = deepReady?.ready === true;
                const missing = deepReady?.missing_required || [];
                const tooltip = ready
                  ? "Analisa mendalam: re-validasi item unresolved vs GANTUNG/PENDING, TMP, KAS"
                  : `Jalankan dulu:\n- ${missing.join("\n- ")}`;
                return (
                  <div className="flex items-center gap-1">
                    <Button
                      data-testid="deep-analyze-btn"
                      variant="outline"
                      onClick={() => triggerDeepAnalysis(false)}
                      disabled={deepLoading || !ready}
                      title={tooltip + "\n\nShift+Click = force refresh cache haystack"}
                      className={`rounded-sm text-xs h-7 gap-1.5 ${ready ? "border-violet-300 text-violet-700 hover:bg-violet-50" : ""}`}
                    >
                      {deepLoading ? <CircleNotch size={12} className="animate-spin" /> : <Lightning size={12} weight="fill" />}
                      {deepLoading ? "Memvalidasi..." : deepAnalysis?.exists ? "Re-Validasi Mendalam" : "Analisa Mendalam"}
                    </Button>
                    {ready && deepAnalysis?.exists && (
                      <button
                        data-testid="deep-analyze-refresh-btn"
                        onClick={() => triggerDeepAnalysis(true)}
                        disabled={deepLoading}
                        title="Force refresh haystack dari Google Sheets (bypass cache 60s)"
                        className="text-[10px] px-1.5 py-1 h-7 rounded-sm border border-violet-200 text-violet-600 hover:bg-violet-50 transition-colors"
                      >
                        ↻
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          {/* Readiness checklist — only show when NOT ready */}
          {deepReady && !deepReady.ready && (
            <div data-testid="deep-readiness-checklist" className="px-4 py-2 border-b border-zinc-200 bg-amber-50/40 text-[11px] text-zinc-600">
              <span className="font-semibold text-amber-700">Analisa Mendalam belum aktif.</span> Jalankan dulu:
              <ul className="mt-1 space-y-0.5 pl-4">
                {(deepReady.missing_required || []).map((m, i) => (
                  <li key={i} className="text-red-600">• {m}</li>
                ))}
                {(deepReady.missing_optional || []).map((m, i) => (
                  <li key={`o-${i}`} className="text-amber-600">• {m} (opsional — rekomendasi)</li>
                ))}
              </ul>
            </div>
          )}
          {aiAnalysis?.exists && (
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${
                    aiAnalysis.risk_score <= 25 ? "bg-emerald-500" :
                    aiAnalysis.risk_score <= 50 ? "bg-amber-500" :
                    aiAnalysis.risk_score <= 75 ? "bg-orange-500" : "bg-red-500"
                  }`} style={{ width: `${Math.min(100, aiAnalysis.risk_score)}%` }} />
                </div>
                <span className="text-xs font-mono text-zinc-500 w-16 text-right">{aiAnalysis.risk_score}/100</span>
              </div>
              <div>
                <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Ringkasan</h4>
                <p className="text-sm text-zinc-700 leading-relaxed">{aiAnalysis.ringkasan}</p>
              </div>
              {aiAnalysis.analisis_adjustment && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-600 mb-1.5">Analisis Adjustment</h4>
                  <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{aiAnalysis.analisis_adjustment}</p>
                </div>
              )}
              {aiAnalysis.analisis_not_found && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-red-600 mb-1.5">Analisis Tidak Ditemukan</h4>
                  <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{aiAnalysis.analisis_not_found}</p>
                </div>
              )}
              {aiAnalysis.rekomendasi && (
                <div>
                  <h4 className="text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-600 mb-1.5">Rekomendasi</h4>
                  <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-line">{aiAnalysis.rekomendasi}</p>
                </div>
              )}
              <p className="text-[10px] text-zinc-400 text-right">
                Dianalisis: {aiAnalysis.analyzed_at ? formatWIB(aiAnalysis.analyzed_at) : "-"} | Model: {aiAnalysis.model || "?"}
              </p>
            </div>
          )}
          {/* ── Deep Analysis Verdicts ── */}
          {deepAnalysis?.exists && (
            <div data-testid="deep-analysis-verdicts" className="border-t border-zinc-200 bg-violet-50/30 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-700 flex items-center gap-1.5">
                  <Lightning size={12} weight="fill" /> Analisa Mendalam ({deepAnalysis.total_needles || 0} item divalidasi)
                </h4>
                <div className="flex items-center gap-2">
                  {haystackCache?.exists && (() => {
                    const age = haystackCache.age_seconds ?? 0;
                    const ttl = haystackCache.ttl_seconds || 60;
                    const pct = age / ttl;
                    let tier = "emerald", label = "fresh";
                    if (!haystackCache.is_fresh) { tier = "zinc"; label = "stale"; }
                    else if (pct > 0.83) { tier = "orange"; label = "soon stale"; }
                    else if (pct > 0.5) { tier = "amber"; label = "aging"; }
                    const colorMap = {
                      emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
                      amber: "bg-amber-50 text-amber-700 border-amber-200",
                      orange: "bg-orange-50 text-orange-700 border-orange-300",
                      zinc: "bg-zinc-50 text-zinc-500 border-zinc-200",
                    };
                    return (
                      <span
                        data-testid="haystack-cache-badge"
                        title={`Haystack di-cache ${age.toFixed(0)}s lalu (TTL ${ttl}s) — ${label}`}
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${colorMap[tier]}`}
                      >
                        📋 cached {age.toFixed(0)}s · {label}
                      </span>
                    );
                  })()}
                  <span className="text-[10px] font-mono text-zinc-400">
                    prompt {deepAnalysis.prompt_version || "?"} · {deepAnalysis.analyzed_at ? formatWIB(deepAnalysis.analyzed_at) : "-"}
                  </span>
                </div>
              </div>
              {(!deepAnalysis.verdicts || deepAnalysis.verdicts.length === 0) && (
                <p className="text-xs text-zinc-500 italic">Tidak ada item unresolved — semua sudah ter-match oleh matcher deterministik. ✓</p>
              )}
              {(() => {
                const allV = deepAnalysis.verdicts || [];
                // Counters across ALL verdicts (not filtered)
                const counters = {
                  match: allV.filter(v => v.verdict === "MATCH_FOUND").length,
                  review: allV.filter(v => v.verdict === "NEEDS_HUMAN_REVIEW").length,
                  no_match: allV.filter(v => v.verdict === "NO_MATCH_CONFIRMED").length,
                  applied: allV.filter(v => v.applied).length,
                };
                // Apply active filter
                const filtered = deepFilter === "all" ? allV
                  : deepFilter === "match" ? allV.filter(v => v.verdict === "MATCH_FOUND")
                  : deepFilter === "review" ? allV.filter(v => v.verdict === "NEEDS_HUMAN_REVIEW")
                  : deepFilter === "no_match" ? allV.filter(v => v.verdict === "NO_MATCH_CONFIRMED")
                  : deepFilter === "applied" ? allV.filter(v => v.applied)
                  : allV;
                const total = filtered.length;
                const pageStart = deepPage * DEEP_PAGE_SIZE;
                const pageEnd = Math.min(pageStart + DEEP_PAGE_SIZE, total);
                const pageVerdicts = filtered.slice(pageStart, pageEnd);
                const totalPages = Math.ceil(total / DEEP_PAGE_SIZE) || 1;
                const filterTabs = [
                  { k: "all", label: "Semua", n: allV.length, color: "violet" },
                  { k: "match", label: "Match", n: counters.match, color: "emerald" },
                  { k: "review", label: "Review", n: counters.review, color: "amber" },
                  { k: "no_match", label: "No-Match", n: counters.no_match, color: "zinc" },
                  { k: "applied", label: "Applied", n: counters.applied, color: "emerald" },
                ];
                const colorClass = {
                  violet: "border-violet-400 bg-violet-600 text-white",
                  emerald: "border-emerald-400 bg-emerald-600 text-white",
                  amber: "border-amber-400 bg-amber-600 text-white",
                  zinc: "border-zinc-400 bg-zinc-700 text-white",
                };
                return (
                  <>
                    {/* Filter toolbar */}
                    {allV.length > 0 && (
                      <div data-testid="deep-filter-tabs" className="flex items-center gap-1 flex-wrap">
                        {filterTabs.map(t => {
                          const active = deepFilter === t.k;
                          return (
                            <button
                              key={t.k}
                              data-testid={`deep-filter-${t.k}`}
                              onClick={() => setDeepFilter(t.k)}
                              disabled={t.n === 0 && t.k !== "all"}
                              className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${
                                active
                                  ? colorClass[t.color]
                                  : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                              } ${t.n === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}
                            >
                              {t.label} <span className="font-mono font-semibold ml-1">({t.n})</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {filtered.length === 0 && allV.length > 0 && (
                      <p className="text-xs text-zinc-400 italic py-3 text-center">Tidak ada verdict dengan filter "{deepFilter}" ini.</p>
                    )}
                    {pageVerdicts.map((v, localIdx) => {
                      const i = pageStart + localIdx;
                      const verdict = v.verdict || v.status || "?";
                      const badgeColor =
                        verdict === "MATCH_FOUND" ? "bg-emerald-100 text-emerald-700" :
                        verdict === "NO_MATCH_CONFIRMED" ? "bg-zinc-200 text-zinc-700" :
                        "bg-amber-100 text-amber-700";
                      const canApply = verdict === "MATCH_FOUND" && ["high", "medium"].includes(v.confidence) && !v.applied;
                      return (
                        <div key={i} className="bg-white border border-violet-200 rounded-sm p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-mono text-violet-700">{v.needle_ref || `item-${i+1}`}</span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${badgeColor}`}>{verdict}</span>
                            {v.confidence && (
                              <span className="text-[10px] text-zinc-400">confidence: {v.confidence}</span>
                            )}
                            {v.applied && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-600 border border-emerald-200">
                                ✓ Applied by {v.applied.solved_by}
                              </span>
                            )}
                            {canApply && (
                              <button
                                data-testid={`apply-verdict-${v.needle_ref}`}
                                onClick={async () => {
                                  if (!confirm(`Terapkan match ini?\n${v.match_citation?.tab}:row=${v.match_citation?.row}\nAkan mengubah status ${v.needle_ref} jadi 'solved_ai_matched'.`)) return;
                                  try {
                                    await API.post(`/jobs/${id}/deep-analysis/apply/${v.needle_ref}`);
                                    alert("Terapkan sukses. Reload halaman...");
                                    loadDeepAnalysis();
                                    loadJob();
                                  } catch (e) {
                                    alert("Gagal terapkan: " + (e.response?.data?.detail || e.message));
                                  }
                                }}
                                className="ml-auto text-[10px] px-2 py-0.5 rounded-sm bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                              >
                                Terapkan
                              </button>
                            )}
                          </div>
                          {v.reasoning && <p className="text-xs text-zinc-700 leading-relaxed">{v.reasoning}</p>}
                          {v.match_citation && v.match_citation.tab && (
                            <div className="text-[11px] font-mono bg-emerald-50 border-l-2 border-emerald-400 pl-2 py-1">
                              📎 {v.match_citation.tab}:row={v.match_citation.row} · Rp {Number(v.match_citation.amount||0).toLocaleString("id-ID")} · {v.match_citation.date || ""} {v.match_citation.username ? ` · user=${v.match_citation.username}` : ""}
                              {v.match_citation.evidence && <div className="text-zinc-500 italic mt-0.5">{v.match_citation.evidence}</div>}
                            </div>
                          )}
                          {v.alternatives && v.alternatives.length > 0 && (
                            <div className="text-[10px] text-zinc-500">
                              Kandidat alternatif ditolak: {v.alternatives.length}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {totalPages > 1 && (
                      <div data-testid="deep-verdicts-pagination" className="flex items-center justify-between pt-2 border-t border-violet-100">
                        <span className="text-[10px] text-zinc-500">
                          Menampilkan {pageStart + 1}–{pageEnd} dari {total} verdict
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setDeepPage(p => Math.max(0, p - 1))}
                            disabled={deepPage === 0}
                            className="text-[10px] px-2 py-0.5 rounded-sm border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            ← Prev
                          </button>
                          <span className="text-[10px] font-mono text-zinc-600 px-1">
                            {deepPage + 1} / {totalPages}
                          </span>
                          <button
                            onClick={() => setDeepPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={deepPage >= totalPages - 1}
                            className="text-[10px] px-2 py-0.5 rounded-sm border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
            <FunnelSimple size={16} />
            Filter & Sort
            {activeFilterCount > 0 && <span className="bg-zinc-900 text-white text-[10px] px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>}
          </button>
          <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
            <span>Showing {records.length} / {job?.records_total || 0}</span>
            <span>Rp {formatIDR(stats.total_amount || 0)}</span>
            {(filterStatus === "not_found" || filterStatus === "__duplicate__") && (job?.records_total || 0) > 0 && (
              <button
                onClick={() => handleBulkRetryByFilter(filterStatus === "__duplicate__" ? "duplicate" : "not_found")}
                disabled={bulkRetrying}
                data-testid="bulk-retry-by-filter-btn"
                title={`Retry semua ${job.records_total} record ${filterStatus === "__duplicate__" ? "duplikat" : "not found"}`}
                className="text-blue-600 hover:text-blue-800 disabled:opacity-50 flex items-center gap-1 border border-blue-200 px-2 py-0.5 rounded-sm hover:bg-blue-50"
              >
                <ArrowClockwise size={12} className={bulkRetrying ? "animate-spin" : ""} />
                {bulkRetrying ? "Retrying..." : `Retry Semua (${job.records_total})`}
              </button>
            )}
            {activeFilterCount > 0 && <button onClick={resetFilters} className="text-red-500 hover:text-red-700 flex items-center gap-1"><X size={12} /> Reset</button>}
          </div>
        </div>
        {showFilters && (
          <div className="p-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            <FilterSelect label="Record Type" value={filterType} onChange={handleFilterChange(setFilterType)} testId="filter-type" options={[{v:"all",l:"Semua"},{v:"deposit",l:"Deposit"},{v:"withdraw",l:"Withdraw"}]} />
            <FilterSelect label="Klasifikasi" value={filterClassification} onChange={handleFilterChange(setFilterClassification)} testId="filter-class" options={[{v:"all",l:"Semua"},{v:"manual",l:"Manual"},{v:"payment_gateway",l:"PG"},{v:"auto_bonus",l:"Bonus Otomatis"},{v:"adjustment",l:"Adjustment"},{v:"system_bonus",l:"System/Bonus"}]} />
            <FilterSelect label="Status Rekon" value={filterStatus} onChange={handleFilterChange(setFilterStatus)} testId="filter-status" options={[{v:"all",l:"Semua"},{v:"matched_exact",l:"Exact"},{v:"matched_tolerant",l:"Tolerant"},{v:"matched_split",l:"Split"},{v:"matched_pg",l:"PG"},{v:"matched_phase4b_cross_midnight",l:"Phase 4B · Cross-Midnight"},{v:"matched_phase4b_live_rescue",l:"Phase 4B · Live Rescue"},{v:"matched_gantung_done",l:"Gantung · Done · Verified"},{v:"matched_gantung_done_unverified",l:"Gantung · Done · Unverified"},{v:"matched_gantung_open",l:"Gantung · Open"},{v:"matched_ai",l:"Matched AI"},{v:"auto_bonus",l:"Bonus Otomatis"},{v:"needs_manual_review",l:"Review"},{v:"not_found",l:"Not Found"},{v:"rejected_panel",l:"Rejected"},{v:"resolved",l:"Resolved"},{v:"panel_error",l:"Error"},{v:"pending",l:"Pending"},{v:"__duplicate__",l:"Duplikat Baris"},{v:"__cross_midnight__",l:"Cross Midnight"},{v:"__pg_verified__",l:"PG Verified"},{v:"__pg_mismatch__",l:"PG Mismatch"},{v:"__pg_not_found__",l:"PG Not Found"}]} />
            <FilterSelect label="Status Panel" value={filterStatusPanel} onChange={handleFilterChange(setFilterStatusPanel)} testId="filter-panel-status" options={[{v:"all",l:"Semua"},{v:"Confirmed",l:"Confirmed"},{v:"Rejected",l:"Rejected"},{v:"Cancelled",l:"Cancelled"}]} />
            <FilterSelect label="Fund Method" value={filterFundMethod} onChange={handleFilterChange(setFilterFundMethod)} testId="filter-fund" options={[{v:"all",l:"Semua"}, ...(filterOptions.fund_methods||[]).map(f => ({v:f,l:f}))]} />
            <FilterSelect label="Approved By" value={filterApprovedBy} onChange={handleFilterChange(setFilterApprovedBy)} testId="filter-approved" options={[{v:"all",l:"Semua"}, ...(filterOptions.approved_by||[]).map(f => ({v:f,l:f}))]} />
            <div className="space-y-1 col-span-2 md:col-span-2">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 flex items-center justify-between">
                <span>Tanggal</span>
                {(filterDateFrom || filterDateTo) && (
                  <button
                    type="button"
                    onClick={() => { setFilterDateFrom(""); setFilterDateTo(""); setRecordsPage(1); }}
                    className="text-[9px] text-red-500 hover:text-red-700 normal-case tracking-normal font-normal"
                    data-testid="filter-date-clear"
                  >
                    × clear
                  </button>
                )}
              </Label>
              <div className="flex gap-1 items-center">
                <Input
                  type="date"
                  data-testid="filter-date-from"
                  value={filterDateFrom}
                  max={filterDateTo || undefined}
                  onChange={e => { setFilterDateFrom(e.target.value); setRecordsPage(1); }}
                  className="rounded-sm border-zinc-300 h-8 text-xs px-1.5 flex-1 min-w-0"
                  title="Dari tanggal"
                />
                <span className="text-zinc-400 text-xs">→</span>
                <Input
                  type="date"
                  data-testid="filter-date-to"
                  value={filterDateTo}
                  min={filterDateFrom || undefined}
                  onChange={e => { setFilterDateTo(e.target.value); setRecordsPage(1); }}
                  className="rounded-sm border-zinc-300 h-8 text-xs px-1.5 flex-1 min-w-0"
                  title="Sampai tanggal"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Cari</Label>
              <Input data-testid="filter-search" value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Username, ID, bank..." className="rounded-sm border-zinc-300 h-8 text-xs" />
            </div>
          </div>
        )}

        {/* Quick Stats Bar */}
        <div className="px-3 py-1.5 border-t border-zinc-100 bg-white flex gap-4 text-[10px] font-mono text-zinc-500 overflow-x-auto">
          <span>Filtered: <strong className="text-zinc-900">{job?.records_total || 0}</strong></span>
          <span>Deposit: <strong className="text-blue-600">{stats.deposits || 0}</strong></span>
          <span>Withdraw: <strong className="text-amber-600">{stats.withdrawals || 0}</strong></span>
          <span>Manual: <strong className="text-zinc-700">{stats.manual || 0}</strong></span>
          <span>PG: <strong className="text-violet-600">{stats.pg || 0}</strong></span>
        </div>
      </div>

      {/* Records Table */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <SortTh field="username" label="Username" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="transaction_type" label="Tipe" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="classification" label="Klas" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="amount" label="Nominal" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <SortTh field="fund_method" label="Fund Method" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="bank_details" label="Bank Details" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="status_panel" label="Status" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="confirmed_by" label="Approved By" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="final_status" label="Rekon" onSort={handleSort} current={sortField} dir={sortDir} />
                <SortTh field="confidence_score" label="Score" onSort={handleSort} current={sortField} dir={sortDir} align="right" />
                <SortTh field="transaction_date" label="Tanggal" onSort={handleSort} current={sortField} dir={sortDir} />
                <th className="px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Notes</th>
                <th className="px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={13} className="px-4 py-8 text-center text-sm text-zinc-400">Tidak ada data yang sesuai filter</td></tr>
              ) : records.map((rec) => (
                <tr key={rec.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors">
                  <td className="px-3 py-1.5 text-xs font-mono text-zinc-900"><a href={`/records/${rec.id}`} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-blue-700 transition-colors" data-testid={`record-link-${rec.id}`}>{rec.username}</a></td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded-sm ${rec.transaction_type === "deposit" ? "bg-blue-50 text-blue-700" : "bg-amber-50 text-amber-700"}`}>
                      {rec.transaction_type === "deposit" ? "DEPO" : "WD"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-mono uppercase tracking-wider px-1 py-0.5 rounded-sm ${rec.classification === "payment_gateway" ? "bg-violet-50 text-violet-700" : rec.classification === "adjustment" ? "bg-orange-50 text-orange-700" : rec.classification === "system_bonus" ? "bg-indigo-50 text-indigo-700" : "bg-zinc-100 text-zinc-600"}`}>
                      {rec.classification === "payment_gateway" ? "PG" : rec.classification === "auto_bonus" ? "BONUS" : rec.classification === "adjustment" ? "ADJ" : rec.classification === "system_bonus" ? "SYS" : "MAN"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs font-mono text-right">{formatIDR(rec.amount)}</td>
                  <td className="px-3 py-1.5 text-[10px] text-zinc-500 max-w-[120px] truncate">{rec.fund_method || "-"}</td>
                  <td className="px-3 py-1.5 text-[10px] text-zinc-500 max-w-[150px] truncate">{rec.bank_details || "-"}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${(rec.status_panel||"").includes("Confirm") ? "bg-emerald-50 text-emerald-700" : (rec.status_panel||"").includes("Reject") ? "bg-red-50 text-red-700" : "bg-zinc-50 text-zinc-500"}`}>
                      {rec.status_panel || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-[10px] text-zinc-500 max-w-[120px] truncate">{rec.confirmed_by || "-"}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1">
                      <StatusBadge status={rec.final_status} />
                      {rec.pg_verification_status === "pg_verified" && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 font-bold" title="Terverifikasi di PG">PG✓</span>
                      )}
                      {rec.pg_verification_status === "pg_mismatch" && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded-sm bg-red-100 text-red-700 font-bold" title={rec.pg_mismatch_reason || "Amount mismatch"}>PG✗</span>
                      )}
                      {rec.pg_verification_status === "pg_not_found" && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-bold" title="Tidak ditemukan di PG">PG?</span>
                      )}
                      {rec.duplicate_row_warning && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded-sm bg-amber-100 text-amber-700 font-bold" title={rec.duplicate_row_info || "1 baris sheet memiliki beberapa kolom nominal"}>DUP</span>
                      )}
                      {rec.split_detected && (
                        <span className="text-[8px] font-mono px-1 py-0.5 rounded-sm bg-blue-100 text-blue-700 font-bold" title={rec.split_notes || "Nominal split dari beberapa bank"}>SPLIT</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-xs font-mono text-right">{rec.confidence_score}</td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500 whitespace-nowrap">{rec.transaction_date || "-"}</td>
                  <td className="px-2 py-1.5 text-[10px] text-zinc-500 max-w-[150px] truncate" title={rec.notes || ""}>{rec.notes || "-"}</td>
                  <td className="px-2 py-1.5">
                    {(["panel_error", "sheet_error", "not_found"].includes(rec.final_status) || rec.duplicate_row_warning) && (
                      <button
                        data-testid={`retry-record-btn-${rec.id}`}
                        onClick={(e) => { e.stopPropagation(); handleRetryRecord(rec.id); }}
                        disabled={retryingId === rec.id}
                        className="flex items-center gap-1 text-[10px] font-mono text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        title={rec.duplicate_row_warning && !["panel_error", "sheet_error", "not_found"].includes(rec.final_status)
                          ? "Retry matching (cek apakah duplikat sheet sudah dibetulkan)"
                          : "Retry matching"}
                      >
                        <ArrowClockwise size={12} className={retryingId === rec.id ? "animate-spin" : ""} />
                        {retryingId === rec.id ? "..." : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {job.records_pages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-xs text-zinc-500 font-mono">
            Page {job.records_page} / {job.records_pages} ({job.records_total} total records)
          </p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={recordsPage <= 1} onClick={() => setRecordsPage(1)} className="rounded-sm text-xs h-7 px-2">First</Button>
            <Button variant="outline" size="sm" disabled={recordsPage <= 1} onClick={() => setRecordsPage(p => p - 1)} className="rounded-sm text-xs h-7 px-2">Prev</Button>
            {Array.from({ length: Math.min(5, job.records_pages) }, (_, i) => {
              const start = Math.max(1, Math.min(recordsPage - 2, job.records_pages - 4));
              const pg = start + i;
              if (pg > job.records_pages) return null;
              return (
                <Button key={pg} variant={pg === recordsPage ? "default" : "outline"} size="sm"
                  onClick={() => setRecordsPage(pg)}
                  className={`rounded-sm text-xs h-7 w-7 px-0 ${pg === recordsPage ? "bg-zinc-900 text-white" : ""}`}
                >{pg}</Button>
              );
            })}
            <Button variant="outline" size="sm" disabled={recordsPage >= job.records_pages} onClick={() => setRecordsPage(p => p + 1)} className="rounded-sm text-xs h-7 px-2">Next</Button>
            <Button variant="outline" size="sm" disabled={recordsPage >= job.records_pages} onClick={() => setRecordsPage(job.records_pages)} className="rounded-sm text-xs h-7 px-2">Last</Button>
          </div>
        </div>
      )}

      {/* Unmatched Outflows (Reverse Matching: Sheet → Panel) */}
      {job.unmatched_outflows && job.unmatched_outflows.length > 0 && (
        <OutflowTable outflows={job.unmatched_outflows} onRefresh={loadJob} />
      )}

      {/* Phase 4: Cross-Midnight Resolutions (audit + undo) */}
      {cmData && cmData.total > 0 && (
        <div data-testid="cross-midnight-section" className="border border-indigo-300 rounded-sm overflow-hidden">
          <button
            onClick={() => setShowCm(v => !v)}
            data-testid="cm-toggle"
            className="w-full px-4 py-3 bg-indigo-50 hover:bg-indigo-100 border-b border-indigo-200 flex items-center justify-between transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-indigo-600">⏰</span>
              <p className="text-sm font-bold text-indigo-900">
                Auto-Resolutions (Phase 4)
              </p>
              <span className="text-xs text-indigo-700">
                {cmData.active} aktif
                {cmData.undone > 0 && ` · ${cmData.undone} di-undo`}
                {" · total "}{cmData.total}
              </span>
            </div>
            <span className="text-xs text-indigo-600">{showCm ? "▼ Sembunyikan" : "▶ Tampilkan"}</span>
          </button>

          {showCm && (
            <div className="p-3 bg-white">
              <p className="text-[11px] text-zinc-500 mb-2">
                Resolusi otomatis untuk unmatched outflow yang sebenarnya cocok di panel.
                <br/>
                <span className="px-1 bg-indigo-100 text-indigo-700 rounded-sm font-mono">CROSS-MIDNIGHT</span>: panel tx tanggal ≠ confirmed_time (true midnight boundary).
                <span className="ml-2 px-1 bg-amber-100 text-amber-700 rounded-sm font-mono">LIVE RESCUE</span>: same-day, ditemukan via live per-username panel query (±1 hari window, tolerance ±5k/2% biaya admin).
                <span className="ml-2 px-1 bg-sky-100 text-sky-700 rounded-sm font-mono">IN-RANGE</span>: Phase 4A — panel cross-midnight terdeteksi di data scrape (legacy).
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="cm-table">
                  <thead className="bg-zinc-50 text-zinc-600">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Status</th>
                      <th className="px-2 py-1.5 text-left">Type</th>
                      <th className="px-2 py-1.5 text-left">Sheet Tab / Row</th>
                      <th className="px-2 py-1.5 text-left">Username</th>
                      <th className="px-2 py-1.5 text-right">Amount</th>
                      <th className="px-2 py-1.5 text-left">Sheet Date</th>
                      <th className="px-2 py-1.5 text-left">Panel Tx Date</th>
                      <th className="px-2 py-1.5 text-left">Panel Confirmed</th>
                      <th className="px-2 py-1.5 text-right">Gap</th>
                      <th className="px-2 py-1.5 text-center">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cmData.resolutions.slice(cmPage * CM_PAGE_SIZE, (cmPage + 1) * CM_PAGE_SIZE).map((r) => {
                      // Determine type: CROSS-MIDNIGHT (true midnight boundary), LIVE RESCUE
                      // (same-day Phase 4B), or IN-RANGE (Phase 4A — no `source` field)
                      let typeBadge = null;
                      if (r.source === "phase4b_rescue") {
                        typeBadge = <span className="px-1.5 py-0.5 text-[9px] bg-indigo-100 text-indigo-700 rounded-sm font-mono" title="True midnight boundary: panel tx_date ≠ confirmed_time">🌙 CROSS-MIDNIGHT</span>;
                      } else if (r.source === "phase4b_live_rescue") {
                        typeBadge = <span className="px-1.5 py-0.5 text-[9px] bg-amber-100 text-amber-700 rounded-sm font-mono" title="Same-day live panel query, tolerance ±5k/2% admin fee">⚡ LIVE RESCUE</span>;
                      } else {
                        typeBadge = <span className="px-1.5 py-0.5 text-[9px] bg-sky-100 text-sky-700 rounded-sm font-mono" title="Phase 4A — panel cross-midnight terdeteksi di data scrape">🔵 IN-RANGE</span>;
                      }
                      return (
                      <tr key={r.id} data-testid={`cm-row-${r.id}`} className={`border-t border-zinc-100 ${r.undone ? "opacity-50 bg-zinc-50" : "hover:bg-indigo-50/30"}`}>
                        <td className="px-2 py-1.5">
                          {r.undone ? (
                            <span className="px-1.5 py-0.5 text-[9px] bg-zinc-200 text-zinc-600 rounded-sm">UNDONE</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[9px] bg-indigo-100 text-indigo-700 rounded-sm">ACTIVE</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">{typeBadge}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-600">
                          {r.outflow_sheet_tab} / {r.outflow_row}
                        </td>
                        <td className="px-2 py-1.5 font-mono">{r.outflow_username}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{Number(r.outflow_amount).toLocaleString("id-ID")}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{r.outflow_date}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{r.panel_transaction_date}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{r.panel_confirmed_time}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-indigo-600">
                          {r.gap_seconds < 60 ? `${r.gap_seconds}s`
                            : r.gap_seconds < 3600 ? `${Math.floor(r.gap_seconds / 60)}m${r.gap_seconds % 60}s`
                            : `${Math.floor(r.gap_seconds / 3600)}h${Math.floor((r.gap_seconds % 3600) / 60)}m`}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {!r.undone && (
                            <button
                              onClick={() => undoCrossMidnight(r.id)}
                              data-testid={`cm-undo-${r.id}`}
                              className="text-[10px] px-2 py-0.5 text-red-600 hover:bg-red-50 border border-red-200 rounded-sm"
                            >
                              Undo
                            </button>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {cmData.resolutions.length > CM_PAGE_SIZE && (() => {
                const total = cmData.resolutions.length;
                const totalPages = Math.ceil(total / CM_PAGE_SIZE);
                const pageStart = cmPage * CM_PAGE_SIZE;
                const pageEnd = Math.min(pageStart + CM_PAGE_SIZE, total);
                return (
                  <div data-testid="cm-pagination" className="flex items-center justify-between px-4 py-2 border-t border-zinc-200 bg-zinc-50">
                    <span className="text-[10px] text-zinc-500">
                      Menampilkan {pageStart + 1}–{pageEnd} dari {total} resolusi
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCmPage(p => Math.max(0, p - 1))}
                        disabled={cmPage === 0}
                        className="text-[10px] px-2 py-0.5 rounded-sm border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        ← Prev
                      </button>
                      <span className="text-[10px] font-mono text-zinc-600 px-1">
                        {cmPage + 1} / {totalPages}
                      </span>
                      <button
                        onClick={() => setCmPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={cmPage >= totalPages - 1}
                        className="text-[10px] px-2 py-0.5 rounded-sm border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Saldo Internal Section */}
      <div className="border border-zinc-200 rounded-sm">
        <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-700">Saldo Internal</h3>
            {saldoData && (
              <span className="text-[10px] font-mono text-zinc-400">
                {saldoData.summary?.total_matched || 0} matched | {saldoData.summary?.total_unmatched || 0} unmatched | {saldoData.summary?.total_suntik || 0} suntik
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="load-saldo-btn" variant="outline" onClick={() => loadSaldo(false)} disabled={saldoLoading}
              className="rounded-sm text-xs h-7 gap-1">
              {saldoLoading ? "Loading..." : saldoData ? "Muat" : "Muat Data"}
            </Button>
            {saldoData && (
              <Button data-testid="refresh-saldo-btn" variant="outline" onClick={() => loadSaldo(true)} disabled={saldoLoading}
                className="rounded-sm text-xs h-7 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50">
                <ArrowClockwise size={12} />
                {saldoLoading ? "..." : "Refresh Sheets"}
              </Button>
            )}
          </div>
        </div>

        {showSaldo && saldoData && (
          <div className="p-4 space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <div className="border border-zinc-200 rounded-sm p-2">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Matched</p>
                <p className="text-sm font-mono font-bold text-emerald-600">{saldoData.summary.total_matched}</p>
              </div>
              <div className="border border-zinc-200 rounded-sm p-2">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Unmatched</p>
                <p className="text-sm font-mono font-bold text-red-600">{saldoData.summary.total_unmatched}</p>
              </div>
              {(saldoData.summary.cross_date_matches || 0) > 0 && (
                <div className="border border-purple-200 bg-purple-50 rounded-sm p-2">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-purple-500">Cross-Date</p>
                  <p className="text-sm font-mono font-bold text-purple-700">{saldoData.summary.cross_date_matches}</p>
                </div>
              )}
              <div className="border border-zinc-200 rounded-sm p-2">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Suntik Dana</p>
                <p className="text-sm font-mono font-bold text-blue-600">{saldoData.summary.total_suntik}</p>
              </div>
              <div className="border border-zinc-200 rounded-sm p-2">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Total Keluar</p>
                <p className="text-xs font-mono text-zinc-700">Rp {formatIDR(saldoData.summary.total_keluar)}</p>
              </div>
              <div className="border border-zinc-200 rounded-sm p-2">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Total Masuk</p>
                <p className="text-xs font-mono text-zinc-700">Rp {formatIDR(saldoData.summary.total_masuk)}</p>
              </div>
              <div className={`border rounded-sm p-2 ${saldoData.summary.selisih > 0 ? "border-red-300 bg-red-50" : "border-emerald-300 bg-emerald-50"}`}>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Selisih</p>
                <p className={`text-sm font-mono font-bold ${saldoData.summary.selisih > 0 ? "text-red-600" : "text-emerald-600"}`}>
                  {saldoData.summary.selisih > 0 ? `Rp ${formatIDR(saldoData.summary.selisih)}` : "Rp 0"}
                </p>
              </div>
            </div>

            {saldoData.matched?.length > 0 && <SaldoMatchedTable data={saldoData.matched} />}
            {saldoData.unmatched?.length > 0 && <SaldoUnmatchedTable data={saldoData.unmatched} />}
            {saldoData.suntik_dana?.length > 0 && <SuntikDanaTable data={saldoData.suntik_dana} />}

            {/* SUNTIK DANA — TMP Matching Section */}
            {saldoData.suntik_dana?.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wider flex items-center gap-1.5">
                    <ArrowsLeftRight size={13} weight="bold" />
                    Cocokkan TMP
                    {tmpData && (
                      <span className="text-[10px] font-mono bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-sm ml-1">
                        {tmpData.summary.total_matched} match | {tmpData.summary.total_unmatched} unmatch
                      </span>
                    )}
                  </h4>
                  <div className="flex gap-1.5">
                    {tmpData && tmpData.unmatched?.length > 0 && tmpData.summary.total_solved < tmpData.unmatched.length && (
                      <Button data-testid="bulk-solve-tmp-btn" variant="outline" size="sm" onClick={bulkSolveTmp}
                        className="rounded-sm text-[10px] h-6 gap-1 border-amber-300 text-amber-600 hover:bg-amber-50">
                        <CheckCircle size={11} /> Solve Semua
                      </Button>
                    )}
                    <Button data-testid="load-tmp-btn" variant="outline" size="sm" onClick={() => loadTmpMatch(!!tmpData)} disabled={tmpLoading}
                      className="rounded-sm text-[10px] h-6 gap-1 border-amber-300 text-amber-600 hover:bg-amber-50">
                      {tmpLoading ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} />}
                      {tmpLoading ? "Loading..." : tmpData ? "Refresh" : "Cocokkan"}
                    </Button>
                  </div>
                </div>

                {tmpData?.error && (
                  <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-300 rounded-sm text-xs text-amber-700">
                    <Warning size={12} className="inline mr-1" />{tmpData.error}
                  </div>
                )}

                {tmpData && !tmpData.error && (
                  <div className="space-y-3">
                    {tmpData.matched?.length > 0 && <SuntikTmpMatchedTable data={tmpData.matched} />}
                    {tmpData.unmatched?.length > 0 && <SuntikTmpUnmatchedTable data={tmpData.unmatched} onSolve={solveTmpItem} />}
                    {tmpData.matched?.length === 0 && tmpData.unmatched?.length === 0 && (
                      <p className="text-xs text-amber-400 text-center py-3">Tidak ada data SUNTIK DANA untuk dicocokkan.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* JUAL PULSA Section */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-violet-600 uppercase tracking-wider flex items-center gap-1.5">
                  <PaperPlaneTilt size={13} weight="bold" />
                  Jual Pulsa
                  {pulsaData && (
                    <span className="text-[10px] font-mono bg-violet-50 text-violet-600 px-1.5 py-0.5 rounded-sm ml-1">
                      {pulsaData.summary.total_records} record | Rp {formatIDR(pulsaData.summary.total_amount)}
                    </span>
                  )}
                </h4>
                <div className="flex gap-1.5">
                  {pulsaData && pulsaData.records.length > 0 && pulsaData.summary.total_verified < pulsaData.records.length && (
                    <Button data-testid="verify-all-pulsa-btn" variant="outline" size="sm" onClick={verifyAllPulsa}
                      className="rounded-sm text-[10px] h-6 gap-1 border-violet-300 text-violet-600 hover:bg-violet-50">
                      <CheckCircle size={11} /> Verify Semua
                    </Button>
                  )}
                  <Button data-testid="load-pulsa-btn" variant="outline" size="sm" onClick={() => loadPulsa(!!pulsaData)} disabled={pulsaLoading}
                    className="rounded-sm text-[10px] h-6 gap-1 border-violet-300 text-violet-600 hover:bg-violet-50">
                    {pulsaLoading ? <CircleNotch size={11} className="animate-spin" /> : <ArrowClockwise size={11} />}
                    {pulsaLoading ? "Loading..." : pulsaData ? "Refresh" : "Muat Data"}
                  </Button>
                </div>
              </div>

              {pulsaData?.summary?.alert_saldo_over_1m && (
                <div data-testid="pulsa-alert-1m" className="mb-2 flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-300 rounded-sm">
                  <Warning size={14} className="text-red-600 flex-shrink-0" />
                  <div className="text-xs font-semibold text-red-700">
                    Saldo pulsa saat ini melebihi Rp 1.000.000!
                    {pulsaData.summary.saldo_alerts?.map((s, i) => (
                      <span key={i} className="ml-2 inline-flex items-center bg-red-100 text-red-800 px-1.5 py-0.5 rounded-sm font-mono text-[10px]">
                        {s.name}: Rp {formatIDR(s.saldo)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* SIM Saldo Info */}
              {pulsaData?.saldo_sim?.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {pulsaData.saldo_sim.map((s, i) => (
                    <div key={i} className={`border rounded-sm px-3 py-1.5 text-xs ${s.saldo > 1000000 ? 'border-red-300 bg-red-50' : 'border-violet-200 bg-violet-50/30'}`}>
                      <span className="text-zinc-500">{s.name}:</span>
                      <span className={`ml-1 font-mono font-bold ${s.saldo > 1000000 ? 'text-red-700' : 'text-violet-700'}`}>
                        Rp {formatIDR(s.saldo)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {pulsaData && <JualPulsaTable data={pulsaData.records} onVerify={verifyPulsa} />}
            </div>
          </div>
        )}
      </div>

      {/* PG Withdraw Analysis — above Anomali PG */}
      {job.pg_check_available && (
        <div data-testid="orphan-section-wrapper" className="border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50/50">
            <div className="flex items-center gap-2">
              <Warning size={16} className="text-red-500" />
              <h3 className="text-sm font-semibold text-zinc-700">PG Withdraw Analysis</h3>
              {orphanData?.summary && (
                <div className="flex gap-2">
                  {orphanData.summary.total > 0 && (
                    <span className="text-[10px] font-mono bg-red-50 text-red-600 px-2 py-0.5 rounded-sm">
                      Orphan: {orphanData.summary.total}
                    </span>
                  )}
                  {orphanData.summary.backoffice_total > 0 && (
                    <span className="text-[10px] font-mono bg-amber-50 text-amber-600 px-2 py-0.5 rounded-sm">
                      Backoffice: {orphanData.summary.backoffice_total} ({orphanData.summary.backoffice_linked} linked)
                    </span>
                  )}
                  {orphanData.summary.total === 0 && orphanData.summary.backoffice_total === 0 && (
                    <span className="text-[10px] font-mono bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-sm">
                      Tidak ada orphan
                    </span>
                  )}
                </div>
              )}
            </div>
            <Button data-testid="load-orphans-btn" variant="outline" onClick={loadOrphans} disabled={orphanLoading}
              className="rounded-sm text-xs h-7 gap-1.5">
              {orphanLoading ? <CircleNotch size={12} className="animate-spin" /> : <Warning size={12} />}
              {orphanLoading ? "Loading..." : orphanData ? "Refresh" : "Muat Data"}
            </Button>
          </div>
          {showOrphans && orphanData && (
            <div className="p-4 space-y-4">
              {/* Sub-section 1: Orphan Whitelabel (bahaya) */}
              {orphanData.orphans.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Warning size={12} weight="bold" /> Orphan Whitelabel — Withdraw Tanpa Panel ({orphanData.orphans.length})
                  </h4>
                  <OrphanWithdrawTable orphans={orphanData.orphans} summary={orphanData.summary} />
                </div>
              )}

              {/* Sub-section 2: Backoffice Withdraws */}
              {orphanData.backoffice?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Keyboard size={12} weight="bold" /> Backoffice Withdraws ({orphanData.backoffice.length})
                    {orphanData.summary.backoffice_linked_suntik > 0 && (
                      <span className="text-[10px] font-mono bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded-sm ml-1">
                        {orphanData.summary.backoffice_linked_suntik} suntik
                      </span>
                    )}
                    {orphanData.summary.backoffice_linked_tmp > 0 && (
                      <span className="text-[10px] font-mono bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-sm">
                        {orphanData.summary.backoffice_linked_tmp} TMP
                      </span>
                    )}
                    {orphanData.summary.backoffice_unlinked > 0 && (
                      <span className="text-[10px] font-mono bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-sm">
                        {orphanData.summary.backoffice_unlinked} unlinked
                      </span>
                    )}
                  </h4>
                  <BackofficeWithdrawTable data={orphanData.backoffice} />
                </div>
              )}

              {orphanData.orphans.length === 0 && (!orphanData.backoffice || orphanData.backoffice.length === 0) && (
                <p className="text-xs text-emerald-600 text-center py-4">Semua PG withdraw tercatat. Tidak ada orphan ditemukan.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* PG Anomali Section — rejected in panel but success in PG */}
      {job.pg_check_available && (
        <div data-testid="anomaly-section-wrapper" className="border border-orange-200 rounded-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-orange-200 bg-orange-50/30">
            <div className="flex items-center gap-2">
              <Warning size={16} className="text-orange-500" />
              <h3 className="text-sm font-semibold text-orange-700">Anomali PG — Rejected Panel + Success PG</h3>
              {anomalyData && (
                <span className="text-[10px] font-mono bg-orange-50 text-orange-600 px-2 py-0.5 rounded-sm">
                  {anomalyData.total} anomali | {anomalyData.linked} linked
                </span>
              )}
            </div>
            <Button data-testid="load-anomalies-btn" variant="outline" onClick={loadAnomalies} disabled={anomalyLoading}
              className="rounded-sm text-xs h-7 gap-1.5 border-orange-300 text-orange-700 hover:bg-orange-50">
              {anomalyLoading ? <CircleNotch size={12} className="animate-spin" /> : <Warning size={12} />}
              {anomalyLoading ? "Loading..." : anomalyData ? "Refresh" : "Muat Data"}
            </Button>
          </div>
          {anomalyData && (
            <div className="p-4">
              {anomalyData.anomalies.length === 0 ? (
                <p className="text-xs text-emerald-600 text-center py-4">Tidak ada anomali PG ditemukan.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full"><thead><tr className="bg-orange-50 border-b border-orange-200">
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Username</th>
                    <th className="text-right px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Nominal</th>
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Tanggal</th>
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Transaction ID</th>
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">PG</th>
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Fund Method</th>
                    <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Alasan</th>
                    <th className="text-center px-3 py-1.5 text-[9px] font-semibold uppercase text-orange-600">Adj Linked</th>
                  </tr></thead><tbody>
                    {anomalyData.anomalies.map((a, i) => (
                      <tr key={i} className={`border-b border-orange-100 ${a.adj_linked ? "bg-emerald-50/30" : ""}`}>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-orange-800">{a.username}</td>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-right font-bold text-orange-700">Rp {formatIDR(a.amount)}</td>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-600">{a.transaction_date}</td>
                        <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500">{a.transaction_id}</td>
                        <td className="px-3 py-1.5 text-[10px] text-orange-600">{a.pg_panel_name}</td>
                        <td className="px-3 py-1.5 text-[10px] text-zinc-500">{a.fund_method}</td>
                        <td className="px-3 py-1.5 text-[10px] text-orange-600 max-w-[200px] truncate" title={a.pg_anomaly_reason}>{a.pg_anomaly_reason}</td>
                        <td className="px-3 py-1.5 text-center">
                          {a.adj_linked ? (
                            <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm font-semibold" title={a.adj_info ? `ADJ: ${a.adj_info.notes || a.adj_info.fund_method}` : ""}>
                              LINKED
                            </span>
                          ) : (
                            <span className="text-[9px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-sm font-semibold">
                              UNLINKED
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Step Log (collapsed by default) */}
      {job.logs && job.logs.length > 0 && (
        <details className="border border-zinc-200 rounded-sm">
          <summary className="px-4 py-2 cursor-pointer text-sm font-semibold text-zinc-700 bg-zinc-50">Step Log ({job.logs.length})</summary>
          <div className="max-h-40 overflow-y-auto">
            {job.logs.map((log, i) => (
              <div key={i} className="px-4 py-1 border-b border-zinc-100 flex items-start gap-3 text-xs">
                <span className="font-mono text-zinc-400 whitespace-nowrap">{log.created_at ? formatWIBTime(log.created_at) : ""}</span>
                <span className="font-semibold text-zinc-700 uppercase tracking-wider">{log.action}</span>
                <span className="text-zinc-500">{log.entity_id?.slice(0, 8)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function Pager({ page, total, perPage, onPage }) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  return (
    <div className="px-3 py-2 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between">
      <span className="text-[10px] text-zinc-500 font-mono">{(page-1)*perPage+1}-{Math.min(page*perPage, total)} dari {total}</span>
      <div className="flex gap-1">
        <Button variant="outline" size="sm" disabled={page<=1} onClick={()=>onPage(page-1)} className="rounded-sm h-6 w-6 p-0 text-xs"><CaretLeft size={12}/></Button>
        {Array.from({length:Math.min(5,pages)},(_,i)=>{
          const s=Math.max(1,Math.min(page-2,pages-4)); const p=s+i; if(p>pages) return null;
          return <Button key={p} variant={p===page?"default":"outline"} size="sm" onClick={()=>onPage(p)} className={`rounded-sm h-6 w-6 p-0 text-[10px] ${p===page?"bg-zinc-900 text-white":""}`}>{p}</Button>
        })}
        <Button variant="outline" size="sm" disabled={page>=pages} onClick={()=>onPage(page+1)} className="rounded-sm h-6 w-6 p-0 text-xs"><CaretRight size={12}/></Button>
      </div>
    </div>
  );
}

function OrphanWithdrawTable({ orphans, summary }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("transaction_date");
  const [sortDir, setSortDir] = useState("desc");
  const [staffFilter, setStaffFilter] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("all");
  const pp = 15;

  const staffList = [...new Set(orphans.map(o => o.processed_by || "").filter(Boolean))].sort();
  const recipientNames = [...new Set(orphans.map(o => (o.recipient_name || "").trim()).filter(Boolean))].sort();

  const filtered = orphans.filter(o => {
    if (staffFilter !== "all" && (o.processed_by || "") !== staffFilter) return false;
    if (recipientFilter !== "all" && (o.recipient_name || "").trim() !== recipientFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (o.member_id || "").toLowerCase().includes(q) ||
             (o.whitelabel_prefix || "").toLowerCase().includes(q) ||
             (o.pg_transaction_id || "").toLowerCase().includes(q) ||
             (o.payment_method || "").toLowerCase().includes(q) ||
             (o.recipient_name || "").toLowerCase().includes(q) ||
             (o.account_number || "").toLowerCase().includes(q) ||
             (o.processed_by || "").toLowerCase().includes(q);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortField] || "";
    const bv = b[sortField] || "";
    if (["amount", "net_amount", "fee"].includes(sortField)) {
      return sortDir === "asc" ? (a[sortField] || 0) - (b[sortField] || 0) : (b[sortField] || 0) - (a[sortField] || 0);
    }
    return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const paged = sorted.slice((page - 1) * pp, page * pp);
  const totalFiltered = filtered.length;
  const totalFilteredAmount = filtered.reduce((s, o) => s + (o.net_amount || 0), 0);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => sortField === field ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : null;

  return (
    <div data-testid="orphan-withdraw-section" className="space-y-3">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-red-200 bg-red-50 rounded-sm p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Total Unmatch</p>
          <p className="text-xl font-mono font-bold text-red-700">{summary.total}</p>
        </div>
        <div className="border border-red-200 bg-red-50 rounded-sm p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-red-500">Total Net Amount</p>
          <p className="text-lg font-mono font-bold text-red-700">Rp {formatIDR(summary.total_amount)}</p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Gross Amount</p>
          <p className="text-lg font-mono font-bold text-zinc-700">Rp {formatIDR(summary.total_gross)}</p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Total Fee</p>
          <p className="text-lg font-mono font-bold text-zinc-700">Rp {formatIDR(summary.total_fee)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="orphan-staff-filter" value={staffFilter} onChange={e=>{setStaffFilter(e.target.value);setPage(1)}} className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none h-8">
          <option value="all">Semua Processed By</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="orphan-recipient-filter" value={recipientFilter} onChange={e=>{setRecipientFilter(e.target.value);setPage(1)}} className="border border-zinc-300 rounded-sm px-2 py-1.5 text-xs bg-white outline-none h-8 max-w-[180px]">
          <option value="all">Semua Penerima</option>{recipientNames.map(n=><option key={n} value={n}>{n.length > 25 ? n.substring(0,25)+"..." : n}</option>)}
        </select>
        <Input data-testid="orphan-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Cari member, nama, account, method..." className="text-xs h-8 w-72 rounded-sm" />
        <span className="text-[11px] text-zinc-500 font-mono ml-auto">
          {totalFiltered} transaksi | Rp {formatIDR(totalFilteredAmount)}
        </span>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 cursor-pointer whitespace-nowrap" onClick={() => toggleSort("transaction_date")}>
                  TANGGAL <SortIcon field="transaction_date" />
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">FINISHED</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 cursor-pointer whitespace-nowrap" onClick={() => toggleSort("member_id")}>
                  MEMBER / REF <SortIcon field="member_id" />
                </th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600 cursor-pointer whitespace-nowrap" onClick={() => toggleSort("net_amount")}>
                  NET AMOUNT <SortIcon field="net_amount" />
                </th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600 whitespace-nowrap">FEE</th>
                <th className="px-3 py-2 text-right font-semibold text-zinc-600 cursor-pointer whitespace-nowrap" onClick={() => toggleSort("amount")}>
                  GROSS <SortIcon field="amount" />
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">METHOD</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">PENERIMA</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">ACCOUNT NO</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">INTEGRATION</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">PROCESSED BY</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600 whitespace-nowrap">PG TX ID</th>
              </tr>
            </thead>
            <tbody>
              {paged.map((o, i) => (
                <tr key={o.pg_transaction_id || i} className="border-b border-zinc-100 hover:bg-red-50/30">
                  <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{o.transaction_date || "-"}</td>
                  <td className="px-3 py-2 font-mono text-zinc-400 whitespace-nowrap">{o.finished_date || "-"}</td>
                  <td className="px-3 py-2 font-mono font-semibold text-zinc-900">{o.member_id || o.whitelabel_prefix || "-"}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-red-700">{formatIDR(o.net_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">{formatIDR(o.fee)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-600">{formatIDR(o.amount)}</td>
                  <td className="px-3 py-2 text-zinc-600 whitespace-nowrap">{o.payment_method || "-"}</td>
                  <td className="px-3 py-2 text-zinc-700 max-w-[150px] truncate" title={o.recipient_name}>{o.recipient_name || "-"}</td>
                  <td className="px-3 py-2 font-mono text-zinc-500 whitespace-nowrap">{o.account_number || "-"}</td>
                  <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{o.integration_type || "-"}</td>
                  <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{o.processed_by || "-"}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-400 max-w-[180px] truncate" title={o.pg_transaction_id}>
                    {o.pg_transaction_id || "-"}
                  </td>
                </tr>
              ))}
              {paged.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-400">Tidak ada withdraw unmatch ditemukan</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={totalFiltered} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function OutflowTable({ outflows, onRefresh }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [tab, setTab] = useState("all");
  const [solvedFilter, setSolvedFilter] = useState("unsolved");
  const [solvedIds, setSolvedIds] = useState(new Set());
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [solving, setSolving] = useState(false);
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  // Helper: detect auto-resolved outflow (Phase 4 system labels)
  // vs manually solved by operator (email format).
  const AUTO_SOLVERS = ["auto_cross_midnight", "phase4b_live_query", "phase4b_live_rescue"];
  const isAutoSolved = (o) => AUTO_SOLVERS.includes(o?.solved_by || "");
  const formatSolvedBy = (o) => {
    if (!o?.solved_by) return "-";
    const map = {
      "auto_cross_midnight": "Phase 4A · Cross-Midnight",
      "phase4b_live_query": "Phase 4B · Live Query",
      "phase4b_live_rescue": "Phase 4B · Live Rescue",
    };
    return map[o.solved_by] || o.solved_by;
  };

  // Load solved status from outflows data
  useEffect(() => {
    const solved = new Set(outflows.filter(o => o.solved).map(o => o.id));
    setSolvedIds(solved);
  }, [outflows]);

  const tabs = [...new Set(outflows.map(o => o.sheet_tab))].sort();
  const staffList = [...new Set(outflows.map(o => o.sheet_staff || "").filter(Boolean))].sort();
  const [staffFilter, setStaffFilter] = useState("all");

  const filtered = outflows.filter(o => {
    if (severity !== "all" && o.severity !== severity) return false;
    if (tab !== "all" && o.sheet_tab !== tab) return false;
    if (staffFilter !== "all" && (o.sheet_staff || "") !== staffFilter) return false;
    if (solvedFilter === "unsolved" && solvedIds.has(o.id)) return false;
    if (solvedFilter === "solved" && !solvedIds.has(o.id)) return false;
    if (solvedFilter === "solved_auto" && (!solvedIds.has(o.id) || !isAutoSolved(o))) return false;
    if (solvedFilter === "solved_manual" && (!solvedIds.has(o.id) || isAutoSolved(o))) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(o.sheet_username||"").toLowerCase().includes(q) && !(o.sheet_bank||"").toLowerCase().includes(q) && !(o.sheet_member_bank||"").toLowerCase().includes(q) && !(o.sheet_staff||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? (a.sheet_amount||0) - (b.sheet_amount||0) : (b.sheet_amount||0) - (a.sheet_amount||0)) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  const toggleBulk = (id) => {
    setBulkSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const handleSolve = async (ids) => {
    setSolving(true);
    try {
      await API.post("/records/outflow/solve", { outflow_ids: ids, notes: "Reviewed & confirmed by admin" });
      setSolvedIds(prev => { const n = new Set(prev); ids.forEach(id => n.add(id)); return n; });
      setBulkSelected(new Set());
      // Refresh job — supaya badge "Outflow" di header langsung sync dengan
      // hasil server (unmatched_outflow_count dihitung live di GET /jobs/{id}).
      if (onRefresh) onRefresh();
    } catch (e) { alert("Gagal menandai solved"); }
    finally { setSolving(false); }
  };

  const handleUnsolve = async (id) => {
    setSolving(true);
    try {
      await API.post("/records/outflow/solve", { outflow_ids: [id], notes: "__unsolve__" });
      setSolvedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      if (onRefresh) onRefresh();
    } catch (e) { alert("Gagal mengembalikan"); }
    finally { setSolving(false); }
  };

  const unsolved = filtered.filter(o => !solvedIds.has(o.id));

  return (
    <div data-testid="unmatched-outflows-section" className="border-2 border-red-300 rounded-sm overflow-hidden">
      <div className="px-4 py-3 bg-red-600 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Warning size={18} weight="fill" />
          <div>
            <p className="text-sm font-bold">Unmatched Outflow</p>
            <p className="text-[10px] text-red-100">{outflows.length} total | {unsolved.length} belum dicek | {solvedIds.size} solved</p>
          </div>
        </div>
        {bulkSelected.size > 0 && (
          <Button onClick={() => handleSolve([...bulkSelected])} disabled={solving}
            className="bg-white text-red-700 hover:bg-red-50 rounded-sm gap-1 text-xs h-7">
            <CheckCircle size={12} /> Solved ({bulkSelected.size})
          </Button>
        )}
      </div>
      <div className="px-3 py-2 bg-red-50 border-b border-red-200 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Solved status quick toggle */}
          <div data-testid="outflow-filter-solved" className="flex items-center gap-1 flex-wrap">
            {(() => {
              const solvedAutoCount = outflows.filter(o => solvedIds.has(o.id) && isAutoSolved(o)).length;
              const solvedManualCount = outflows.filter(o => solvedIds.has(o.id) && !isAutoSolved(o)).length;
              const counts = {
                all: outflows.length,
                unsolved: outflows.filter(o => !solvedIds.has(o.id)).length,
                solved: outflows.filter(o => solvedIds.has(o.id)).length,
                solved_auto: solvedAutoCount,
                solved_manual: solvedManualCount,
              };
              const tabsCfg = [
                { k: "all", label: "Semua", color: "zinc" },
                { k: "unsolved", label: "Belum Dicek", color: "red" },
                { k: "solved", label: "Solved (semua)", color: "emerald" },
                { k: "solved_auto", label: "🤖 Auto", color: "emerald" },
                { k: "solved_manual", label: "👤 Manual", color: "emerald" },
              ];
              return tabsCfg.map(t => {
                const active = solvedFilter === t.k;
                const cm = {
                  zinc: "border-zinc-400 bg-zinc-700 text-white",
                  red: "border-red-500 bg-red-600 text-white",
                  emerald: "border-emerald-400 bg-emerald-600 text-white",
                };
                return (
                  <button key={t.k}
                    data-testid={`outflow-solved-${t.k}`}
                    onClick={() => { setSolvedFilter(t.k); setPage(1); }}
                    disabled={counts[t.k] === 0 && t.k !== "all"}
                    className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active ? cm[t.color] : "border-red-200 bg-white text-red-700 hover:bg-red-50"} ${counts[t.k] === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}>
                    {t.label} <span className="font-mono font-semibold ml-1">({counts[t.k]})</span>
                  </button>
                );
              });
            })()}
          </div>
          <div className="h-5 w-px bg-red-300" />
          {/* Severity quick toggle */}
          <div data-testid="outflow-filter-severity" className="flex items-center gap-1">
            {(() => {
              const counts = {
                all: outflows.length,
                critical: outflows.filter(o => o.severity === "critical").length,
                warning: outflows.filter(o => o.severity === "warning").length,
              };
              const tabsCfg = [
                { k: "all", label: "Semua Severity", color: "zinc" },
                { k: "critical", label: "Kritis", color: "red" },
                { k: "warning", label: "Peringatan", color: "amber" },
              ];
              return tabsCfg.map(t => {
                const active = severity === t.k;
                const cm = {
                  zinc: "border-zinc-400 bg-zinc-700 text-white",
                  red: "border-red-500 bg-red-600 text-white",
                  amber: "border-amber-400 bg-amber-600 text-white",
                };
                return (
                  <button key={t.k}
                    data-testid={`outflow-severity-${t.k}`}
                    onClick={() => { setSeverity(t.k); setPage(1); }}
                    disabled={counts[t.k] === 0 && t.k !== "all"}
                    className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${active ? cm[t.color] : "border-red-200 bg-white text-red-700 hover:bg-red-50"} ${counts[t.k] === 0 && t.k !== "all" ? "opacity-40 cursor-not-allowed" : ""}`}>
                    {t.label} <span className="font-mono font-semibold ml-1">({counts[t.k]})</span>
                  </button>
                );
              });
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="outflow-tab-filter" value={tab} onChange={e=>{setTab(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Tab</option>{tabs.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="outflow-staff-filter" value={staffFilter} onChange={e=>{setStaffFilter(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari username, bank, staff..." className="rounded-sm border-red-300 h-7 text-xs w-52" />
          <span className="text-[10px] text-red-500 font-mono">{filtered.length}/{outflows.length}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-red-50 border-b border-red-200">
            <th className="px-2 py-1.5 w-8">
              <button onClick={() => setBulkSelected(prev => prev.size === paged.filter(o=>!solvedIds.has(o.id)).length ? new Set() : new Set(paged.filter(o=>!solvedIds.has(o.id)).map(o=>o.id)))}>
                <Square size={14} className="text-red-400" />
              </button>
            </th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Severity</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Username</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-red-700" />
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Tanggal</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Staff</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Tab</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Row</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Bank</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Keterangan</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Status</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-700">Alert</th>
            <th className="px-2 py-1.5 w-16" />
          </tr></thead>
          <tbody>{paged.length===0?(
            <tr><td colSpan={13} className="px-4 py-6 text-center text-xs text-red-400">Tidak ada data sesuai filter</td></tr>
          ):paged.map((of,i)=>{
            const isSolved = solvedIds.has(of.id);
            return (
              <tr key={of.id||i} className={`border-b border-red-100 transition-colors ${isSolved ? "bg-emerald-50 opacity-70" : of.severity==="critical" ? "bg-red-100" : "bg-amber-50"}`}>
                <td className="px-2 py-1.5">
                  {!isSolved && (
                    <button onClick={()=>toggleBulk(of.id)}>
                      {bulkSelected.has(of.id) ? <CheckSquare size={14} weight="fill" className="text-zinc-900"/> : <Square size={14} className="text-red-400"/>}
                    </button>
                  )}
                </td>
                <td className="px-3 py-1.5">
                  {isSolved ? (
                    (() => {
                      const auto = isAutoSolved(of);
                      const tip = `${formatSolvedBy(of)}${of.solved_at ? " · " + new Date(of.solved_at).toLocaleString("id-ID") : ""}${of.solved_notes ? "\n" + of.solved_notes : ""}`;
                      return (
                        <span
                          title={tip}
                          className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm font-bold inline-flex items-center gap-1 cursor-help ${auto ? "bg-emerald-600 text-white" : "bg-emerald-500 text-white"}`}
                          data-testid={`outflow-solved-badge-${auto ? "auto" : "manual"}`}
                        >
                          {auto ? "🤖" : "👤"} SOLVED · {auto ? "AUTO" : "MANUAL"}
                        </span>
                      );
                    })()
                  ) : (
                    <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm font-bold ${of.severity === "critical" ? "bg-red-600 text-white" : "bg-amber-500 text-white"}`}>
                      {of.severity === "critical" ? "KRITIS" : "PERINGATAN"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs font-mono text-red-900 font-semibold">{of.sheet_username}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-right font-bold text-red-800">{formatIDR(of.sheet_amount)}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-red-700">{of.sheet_date}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-500">{of.sheet_staff || "-"}</td>
                <td className="px-3 py-1.5 text-[10px] text-red-600">{of.sheet_tab}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-400">{of.sheet_row || "-"}</td>
                <td className="px-3 py-1.5 text-[10px] text-red-600">{of.sheet_bank}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-500 max-w-[120px] truncate" title={of.sheet_member_bank}>{of.sheet_member_bank || "-"}</td>
                <td className="px-3 py-1.5"><span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded-sm ${of.panel_status==="rejected" ? "bg-red-200 text-red-800" : "bg-zinc-200 text-zinc-700"}`}>{of.panel_status==="rejected" ? "REJECTED" : "NOT IN PANEL"}</span></td>
                <td className="px-3 py-1.5 text-[10px] text-red-700 font-semibold max-w-[180px] truncate">{of.alert}</td>
                <td className="px-2 py-1.5">
                  {!isSolved ? (
                    <button onClick={()=>handleSolve([of.id])} disabled={solving}
                      className="text-[9px] font-mono text-emerald-600 hover:text-emerald-800 font-bold" title="Tandai sudah dicek">
                      SOLVE
                    </button>
                  ) : (
                    <button onClick={()=>handleUnsolve(of.id)} disabled={solving}
                      className="text-[9px] font-mono text-red-500 hover:text-red-700 font-bold" title="Kembalikan">
                      UNSOLVE
                    </button>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      <Pager page={page} total={filtered.length} perPage={pp} onPage={setPage} />
    </div>
  );
}

function SaldoMatchedTable({ data }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [staff, setStaff] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  const staffList = [...new Set(data.map(p => p.staff || "").filter(Boolean))].sort();

  const filtered = data.filter(p => {
    if (type !== "all" && p.type !== type) return false;
    if (staff !== "all" && (p.staff || "") !== staff) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(p.keterangan||"").toLowerCase().includes(q) && !(p.from_bank||"").toLowerCase().includes(q) && !(p.to_bank||"").toLowerCase().includes(q) && !(p.staff||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.amount - b.amount : b.amount - a.amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-zinc-600 uppercase tracking-wider">Transfer Internal — Matched ({data.length})</h4>
        <div className="flex gap-2">
          <select value={type} onChange={e=>{setType(e.target.value);setPage(1)}} className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Tipe</option><option value="within_row">1 Baris</option><option value="cross_tab">Antar Tab</option><option value="cross_date">Cross-Date</option>
          </select>
          <select data-testid="matched-staff-filter" value={staff} onChange={e=>{setStaff(e.target.value);setPage(1)}} className="border border-zinc-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>
            {staffList.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari bank, keterangan..." className="rounded-sm border-zinc-300 h-7 text-xs w-40" />
          <span className="text-[10px] text-zinc-400 font-mono self-center">{filtered.length}/{data.length}</span>
        </div>
      </div>
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-zinc-50 border-b border-zinc-200">
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Tanggal</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Keterangan</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Staff</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Dari</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Ke</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-zinc-500" />
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Tipe</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-zinc-500">Status</th>
          </tr></thead><tbody>
            {paged.length===0?(<tr><td colSpan={8} className="px-4 py-6 text-center text-xs text-zinc-400">Tidak ada data</td></tr>):
            paged.map((p,i)=>(
              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-600">{p.date?.split(" ")[0]}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-700 max-w-[200px] truncate" title={p.keterangan}>{p.keterangan}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500">{p.staff || "-"}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-red-600">{p.from_bank} <span className="text-zinc-400">({p.from_tab})</span> <span className="text-zinc-300">R{p.from_row}</span></td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-emerald-600">{p.to_bank} <span className="text-zinc-400">({p.to_tab})</span> <span className="text-zinc-300">R{p.to_row}</span></td>
                <td className="px-3 py-1.5 text-xs font-mono text-right text-zinc-900">Rp {formatIDR(p.amount)}</td>
                <td className="px-3 py-1.5"><span className={`text-[9px] font-mono px-1 py-0.5 rounded-sm ${p.type==="within_row"?"bg-blue-50 text-blue-700":p.type==="cross_date"?"bg-purple-50 text-purple-700":"bg-purple-50 text-purple-700"}`}>{p.type==="within_row"?"1 BARIS":p.type==="cross_date"?"CROSS-DATE":"ANTAR TAB"}</span></td>
                <td className="px-3 py-1.5"><span className={`text-[9px] font-mono px-1 py-0.5 rounded-sm ${p.diff===0?"bg-emerald-50 text-emerald-700":"bg-amber-50 text-amber-700"}`}>{p.diff===0?"EXACT":`±Rp ${formatIDR(p.diff)}`}</span>
                {p.cross_date_note && <p className="text-[8px] text-purple-500 mt-0.5">{p.cross_date_note}</p>}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function SaldoUnmatchedTable({ data }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [dir, setDir] = useState("all");
  const [staff, setStaff] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const [solvedKeys, setSolvedKeys] = useState(new Set());
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [solving, setSolving] = useState(false);
  const pp = 10;

  const getKey = (u) => u.solve_key || `${u.tab}__${u.keterangan}__${u.amount}__${u.bank}`;

  // Init solved from server data
  useEffect(() => {
    const keys = new Set(data.filter(u => u.solved).map(u => getKey(u)));
    setSolvedKeys(keys);
  }, [data]);

  const solvedCount = data.filter(u => solvedKeys.has(getKey(u))).length;

  const staffList = [...new Set(data.map(u => u.staff || "").filter(Boolean))].sort();

  const filtered = data.filter(u => {
    if (dir !== "all" && u.type !== dir) return false;
    if (staff !== "all" && (u.staff || "") !== staff) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(u.keterangan||"").toLowerCase().includes(q) && !(u.bank||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? Math.abs(a.amount) - Math.abs(b.amount) : Math.abs(b.amount) - Math.abs(a.amount)) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  const toggleBulk = (key) => {
    setBulkSelected(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  };

  const handleSolve = async (keys) => {
    setSolving(true);
    try {
      await API.post("/records/saldo-internal/solve", { keys, solved: true, notes: "Reviewed" });
      setSolvedKeys(prev => { const n = new Set(prev); keys.forEach(k => n.add(k)); return n; });
      setBulkSelected(new Set());
    } catch (e) { alert("Gagal"); }
    finally { setSolving(false); }
  };

  const handleUnsolve = async (key) => {
    setSolving(true);
    try {
      await API.post("/records/saldo-internal/solve", { keys: [key], solved: false });
      setSolvedKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
    } catch (e) { alert("Gagal"); }
    finally { setSolving(false); }
  };

  const unsolvedCount = data.filter(u => !solvedKeys.has(getKey(u))).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wider">
          Unmatched ({data.length}) {solvedCount > 0 && <span className="text-emerald-600 font-normal">| {solvedCount} solved</span>}
        </h4>
        <div className="flex gap-2">
          <select value={dir} onChange={e=>{setDir(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Arah</option><option value="keluar">Keluar</option><option value="masuk">Masuk</option>
          </select>
          <select data-testid="unmatched-staff-filter" value={staff} onChange={e=>{setStaff(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>
            {staffList.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari bank, keterangan..." className="rounded-sm border-red-300 h-7 text-xs w-40" />
          {bulkSelected.size > 0 && (
            <Button onClick={() => handleSolve([...bulkSelected])} disabled={solving} variant="outline" className="rounded-sm gap-1 text-xs h-7 text-emerald-700 border-emerald-300">
              <CheckCircle size={12} /> Solved ({bulkSelected.size})
            </Button>
          )}
        </div>
      </div>
      <div className="border border-red-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-red-50 border-b border-red-200">
            <th className="px-2 py-1.5 w-8">
              <button onClick={() => {
                const unsolvedOnPage = paged.map(u => getKey(u)).filter(k => !solvedKeys.has(k));
                setBulkSelected(prev => prev.size === unsolvedOnPage.length ? new Set() : new Set(unsolvedOnPage));
              }}><Square size={14} className="text-red-400" /></button>
            </th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Tanggal</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Keterangan</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Staff</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Bank</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Tab</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Row</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-red-500" />
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-red-500">Arah</th>
            <th className="px-2 py-1.5 w-16" />
          </tr></thead><tbody>
            {paged.length===0?(<tr><td colSpan={10} className="px-4 py-6 text-center text-xs text-red-400">Tidak ada data</td></tr>):
            paged.map((u,i)=>{
              const key = getKey(u);
              const isSolved = solvedKeys.has(key);
              return (
                <tr key={i} className={`border-b border-red-100 transition-colors ${isSolved ? "bg-emerald-50 opacity-70" : ""}`}>
                  <td className="px-2 py-1.5">
                    {!isSolved && (
                      <button onClick={()=>toggleBulk(key)}>
                        {bulkSelected.has(key) ? <CheckSquare size={14} weight="fill" className="text-zinc-900"/> : <Square size={14} className="text-red-400"/>}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-red-600">{u.date?.split(" ")[0]}</td>
                  <td className="px-3 py-1.5 text-[10px] text-red-700">{u.keterangan}</td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500">{u.staff || "-"}</td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-red-600">{u.bank}</td>
                  <td className="px-3 py-1.5 text-[10px] text-red-500">{u.tab}</td>
                  <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-400">{u.row || "-"}</td>
                  <td className="px-3 py-1.5 text-xs font-mono text-right text-red-700 font-bold">Rp {formatIDR(u.amount)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[9px] font-mono px-1 py-0.5 rounded-sm ${isSolved ? "bg-emerald-100 text-emerald-700" : u.type==="masuk" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {isSolved ? "SOLVED" : u.type==="masuk" ? "MASUK" : "KELUAR"}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    {!isSolved ? (
                      <button onClick={()=>handleSolve([key])} disabled={solving}
                        className="text-[9px] font-mono text-emerald-600 hover:text-emerald-800 font-bold">SOLVE</button>
                    ) : (
                      <button onClick={()=>handleUnsolve(key)} disabled={solving}
                        className="text-[9px] font-mono text-red-500 hover:text-red-700 font-bold">UNSOLVE</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function SuntikDanaTable({ data }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tabFilter, setTabFilter] = useState("all");
  const [staff, setStaff] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  const flat = data.flatMap(s => (s.entries||[]).map(e => ({ ...s, bank: e.bank_column, amount: Math.abs(e.amount) })));
  const tabs = [...new Set(flat.map(f => f.sheet_tab))].sort();
  const staffList = [...new Set(flat.map(f => f.staff || "").filter(Boolean))].sort();
  const filtered = flat.filter(f => {
    if (tabFilter !== "all" && f.sheet_tab !== tabFilter) return false;
    if (staff !== "all" && (f.staff || "") !== staff) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(f.keterangan||"").toLowerCase().includes(q) && !(f.bank||"").toLowerCase().includes(q) && !(f.staff||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.amount - b.amount : b.amount - a.amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Suntik Dana — Dana Eksternal ({flat.length})</h4>
        <div className="flex gap-2">
          <select value={tabFilter} onChange={e=>{setTabFilter(e.target.value);setPage(1)}} className="border border-blue-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Tab</option>{tabs.map(t=><option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="suntik-staff-filter" value={staff} onChange={e=>{setStaff(e.target.value);setPage(1)}} className="border border-blue-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari bank, staff..." className="rounded-sm border-blue-300 h-7 text-xs w-40" />
        </div>
      </div>
      <div className="border border-blue-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-blue-50 border-b border-blue-200">
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Tanggal</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Keterangan</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Staff</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Bank</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Tab</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-blue-500">Row</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-blue-500" />
          </tr></thead><tbody>
            {paged.length===0?(<tr><td colSpan={7} className="px-4 py-6 text-center text-xs text-blue-400">Tidak ada data</td></tr>):
            paged.map((f,i)=>(
              <tr key={i} className="border-b border-blue-100">
                <td className="px-3 py-1.5 text-[10px] font-mono text-blue-600">{f.date?.split(" ")[0]}</td>
                <td className="px-3 py-1.5 text-[10px] text-blue-700">{f.keterangan}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-500">{f.staff}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-blue-600">{f.bank}</td>
                <td className="px-3 py-1.5 text-[10px] text-blue-500">{f.sheet_tab}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-400">{f.sheet_row || "-"}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-right text-blue-700 font-bold">Rp {formatIDR(f.amount)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>

      {/* Captcha image is now cropped to form only — no zoom needed */}
    </div>
  );
}

function JualPulsaTable({ data, onVerify }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [staff, setStaff] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  const staffList = [...new Set(data.map(r => r.staff || "").filter(Boolean))].sort();

  const filtered = data.filter(r => {
    if (staff !== "all" && (r.staff || "") !== staff) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.keterangan||"").toLowerCase().includes(q)
      || (r.provider||"").toLowerCase().includes(q)
      || (r.nomor||"").toLowerCase().includes(q)
      || (r.staff||"").toLowerCase().includes(q);
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.amount - b.amount : b.amount - a.amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  if (!data.length) return <p className="text-xs text-violet-400 text-center py-3">Belum ada data Jual Pulsa.</p>;

  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-2">
        <select data-testid="pulsa-staff-filter" value={staff} onChange={e=>{setStaff(e.target.value);setPage(1)}} className="border border-violet-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
          <option value="all">Semua Staff</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <Input data-testid="pulsa-search" value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari provider, nomor, staff..." className="rounded-sm border-violet-300 h-7 text-xs w-48" />
      </div>
      <div className="border border-violet-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-violet-50 border-b border-violet-200">
            <th className="text-center px-2 py-1.5 text-[9px] font-semibold uppercase text-violet-500 w-10">V</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Tanggal</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Staff</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Keterangan</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Provider</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Nomor</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Kolom SIM</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Tab</th>
            <th className="text-left px-3 py-1.5 text-[9px] font-semibold uppercase text-violet-500">Row</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-violet-500" />
          </tr></thead><tbody>
            {paged.length===0?(<tr><td colSpan={10} className="px-4 py-6 text-center text-xs text-violet-400">Tidak ada data</td></tr>):
            paged.map((r,i)=>(
              <tr key={i} className={`border-b border-violet-100 ${r.verified ? "bg-emerald-50/40" : ""}`}>
                <td className="text-center px-2 py-1.5">
                  <button data-testid={`verify-pulsa-${i}`} onClick={() => onVerify(r.verify_key, !r.verified)}
                    className={`transition-colors ${r.verified ? "text-emerald-600" : "text-zinc-300 hover:text-violet-500"}`}>
                    {r.verified ? <CheckCircle size={15} weight="fill" /> : <Square size={15} />}
                  </button>
                </td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-violet-600">{r.date?.split(" ")[0]}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-500">{r.staff || "-"}</td>
                <td className="px-3 py-1.5 text-[10px] text-violet-700">{r.keterangan}</td>
                <td className="px-3 py-1.5 text-[10px] font-semibold text-violet-600">{r.provider}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-500">{r.nomor}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-500">{r.amount_column}</td>
                <td className="px-3 py-1.5 text-[10px] text-violet-500">{r.sheet_tab}</td>
                <td className="px-3 py-1.5 text-[10px] font-mono text-zinc-400">{r.sheet_row || "-"}</td>
                <td className="px-3 py-1.5 text-xs font-mono text-right text-violet-700 font-bold">Rp {formatIDR(r.amount)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function SuntikTmpMatchedTable({ data }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  const staffList = [...new Set(data.map(m => m.suntik_staff || "").filter(Boolean))].sort();

  const filtered = data.filter(m => {
    if (staffFilter !== "all" && (m.suntik_staff || "") !== staffFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(m.suntik_bank||"").toLowerCase().includes(q) &&
          !(m.tmp_keterangan||"").toLowerCase().includes(q) &&
          !(m.tmp_bank||"").toLowerCase().includes(q) &&
          !(m.suntik_staff||"").toLowerCase().includes(q) &&
          !(m.suntik_keterangan||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.suntik_amount - b.suntik_amount : b.suntik_amount - a.suntik_amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Matched ({filtered.length}{filtered.length !== data.length ? `/${data.length}` : ""})</h4>
        <div className="flex items-center gap-2">
          <select data-testid="suntik-matched-staff-filter" value={staffFilter} onChange={e=>{setStaffFilter(e.target.value);setPage(1)}} className="border border-emerald-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <Input data-testid="suntik-matched-search" value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari bank, staff, keterangan..." className="rounded-sm border-emerald-300 h-7 text-xs w-52" />
        </div>
      </div>
      <div className="border border-emerald-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-emerald-50 border-b border-emerald-200">
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Tgl Suntik</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Bank (Rekon)</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Staff</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-emerald-500" />
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Tgl TMP</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Ket TMP</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Bank TMP</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Tab</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Row</th>
            <th className="text-center px-2 py-1.5 text-[9px] font-semibold uppercase text-emerald-500">Match</th>
          </tr></thead><tbody>
            {paged.map((m, i) => (
              <tr key={i} className="border-b border-emerald-100">
                <td className="px-2 py-1.5 text-[10px] font-mono text-emerald-700">{m.suntik_date?.split(" ")[0]}</td>
                <td className="px-2 py-1.5 text-[10px] text-emerald-800">{m.suntik_bank}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-600">{m.suntik_staff || "-"}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-right font-bold text-emerald-700">Rp {formatIDR(m.suntik_amount)}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-600">{m.tmp_date}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-700 max-w-[200px] truncate" title={m.tmp_keterangan}>{m.tmp_keterangan}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-600">{m.tmp_bank}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-blue-600">{m.suntik_tab || "-"}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-500">{m.suntik_row || "-"}</td>
                <td className="px-2 py-1.5 text-center">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-mono ${m.bank_match ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {m.bank_match ? "EXACT" : "AMOUNT"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function SuntikTmpUnmatchedTable({ data, onSolve }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [staff, setStaff] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  const staffList = [...new Set(data.map(u => u.staff || "").filter(Boolean))].sort();

  const filtered = data.filter(u => {
    if (filter === "unsolved" && u.solved) return false;
    if (filter === "solved" && !u.solved) return false;
    if (staff !== "all" && (u.staff || "") !== staff) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(u.bank||"").toLowerCase().includes(q) && !(u.keterangan||"").toLowerCase().includes(q) && !(u.staff||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.amount - b.amount : b.amount - a.amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs font-semibold text-red-600 uppercase tracking-wider">
          Suntik Dana Unmatch TMP ({data.filter(u => !u.solved).length} unsolved / {data.length} total)
        </h4>
        <div className="flex gap-2">
          <select value={filter} onChange={e=>{setFilter(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua</option>
            <option value="unsolved">Unsolved</option>
            <option value="solved">Solved</option>
          </select>
          <select data-testid="tmp-unmatch-staff-filter" value={staff} onChange={e=>{setStaff(e.target.value);setPage(1)}} className="border border-red-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
            <option value="all">Semua Staff</option>{staffList.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
          <Input data-testid="tmp-unmatch-search" value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari bank, staff..." className="rounded-sm border-red-300 h-7 text-xs w-40" />
        </div>
      </div>
      <div className="border border-red-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-red-50 border-b border-red-200">
            <th className="text-center px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500 w-10">S</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Tanggal</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Keterangan</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Staff</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Bank (Rekon)</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Tab</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-red-500">Row</th>
            <SortTH label="Nominal" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-red-500" />
          </tr></thead><tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-4 text-center text-xs text-red-400">Tidak ada data</td></tr>
            ) : paged.map((u, i) => (
              <tr key={i} className={`border-b border-red-100 ${u.solved ? "bg-emerald-50/30" : ""}`}>
                <td className="text-center px-2 py-1.5">
                  <button data-testid={`solve-tmp-${i}`} onClick={() => onSolve(u.solve_key, !u.solved)}
                    className={`transition-colors ${u.solved ? "text-emerald-600" : "text-zinc-300 hover:text-red-500"}`}>
                    {u.solved ? <CheckCircle size={15} weight="fill" /> : <Square size={15} />}
                  </button>
                </td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-red-700">{u.date?.split(" ")[0]}</td>
                <td className="px-2 py-1.5 text-[10px] text-red-800 max-w-[180px] truncate" title={u.keterangan}>{u.keterangan}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-500">{u.staff}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-red-700">{u.bank}</td>
                <td className="px-2 py-1.5 text-[10px] text-red-500">{u.tab}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-400">{u.row}</td>
                <td className="px-2 py-1.5 text-xs font-mono text-right text-red-700 font-bold">Rp {formatIDR(u.amount)}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function BackofficeWithdrawTable({ data }) {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [recipientFilter, setRecipientFilter] = useState("all");
  const [sortDir, setSortDir] = useState(null);
  const pp = 10;

  // Build unique recipient names for filter
  const recipientNames = [...new Set(data.map(b => (b.recipient_name || "").trim()).filter(Boolean))].sort();

  const filtered = data.filter(b => {
    if (filter === "linked" && !(b.suntik_linked && b.link_type !== "tmp_masuk_saldo")) return false;
    if (filter === "linked_tmp" && b.link_type !== "tmp_masuk_saldo") return false;
    if (filter === "unlinked" && b.suntik_linked) return false;
    if (recipientFilter !== "all" && (b.recipient_name || "").trim() !== recipientFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(b.recipient_name||"").toLowerCase().includes(q) && !(b.payment_method||"").toLowerCase().includes(q)
        && !(b.account_number||"").toLowerCase().includes(q) && !(b.processed_by||"").toLowerCase().includes(q)) return false;
    }
    return true;
  });
  const sorted = sortDir ? [...filtered].sort((a,b) => sortDir === "asc" ? a.net_amount - b.net_amount : b.net_amount - a.net_amount) : filtered;
  const paged = sorted.slice((page-1)*pp, page*pp);

  return (
    <div>
      <div className="flex items-center justify-end gap-2 mb-2">
        <select data-testid="bo-status-filter" value={filter} onChange={e=>{setFilter(e.target.value);setPage(1)}} className="border border-amber-300 rounded-sm px-2 py-1 text-xs bg-white outline-none">
          <option value="all">Semua</option><option value="linked">Linked (Suntik)</option><option value="linked_tmp">Linked (TMP)</option><option value="unlinked">Unlinked</option>
        </select>
        <select data-testid="bo-recipient-filter" value={recipientFilter} onChange={e=>{setRecipientFilter(e.target.value);setPage(1)}} className="border border-amber-300 rounded-sm px-2 py-1 text-xs bg-white outline-none max-w-[180px]">
          <option value="all">Semua Penerima</option>
          {recipientNames.map(n => <option key={n} value={n}>{n.length > 25 ? n.substring(0,25)+"..." : n}</option>)}
        </select>
        <Input data-testid="bo-search" value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} placeholder="Cari nama, bank, account..." className="rounded-sm border-amber-300 h-7 text-xs w-48" />
      </div>
      <div className="border border-amber-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full"><thead><tr className="bg-amber-50 border-b border-amber-200">
            <th className="text-center px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600 w-16">Status</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">Tanggal</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">Bank Tujuan</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">Nama Penerima</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">No. Rekening</th>
            <SortTH label="Net Amount" field="amount" current={sortDir ? "amount" : null} dir={sortDir} onSort={() => setSortDir(d => d === "desc" ? "asc" : d === "asc" ? null : "desc")} align="right" color="text-amber-600" />
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">Processed By</th>
            <th className="text-left px-2 py-1.5 text-[9px] font-semibold uppercase text-amber-600">Linked Suntik Dana</th>
          </tr></thead><tbody>
            {paged.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-4 text-center text-xs text-amber-400">Tidak ada data</td></tr>
            ) : paged.map((b, i) => (
              <tr key={i} className={`border-b border-amber-100 ${b.suntik_linked ? (b.link_type === "tmp_masuk_saldo" ? "bg-blue-50/30" : "bg-emerald-50/30") : ""}`}>
                <td className="text-center px-2 py-1.5">
                  {b.suntik_linked ? (
                    b.link_type === "tmp_masuk_saldo" ? (
                      <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-sm font-semibold">LINKED (TMP)</span>
                    ) : (
                      <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm font-semibold">LINKED</span>
                    )
                  ) : (
                    <span className="text-[9px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-sm font-semibold">UNLINKED</span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-amber-700">{b.transaction_date?.split(" ")[0]}</td>
                <td className="px-2 py-1.5 text-[10px] text-amber-700 font-semibold">{b.payment_method}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-700">{b.recipient_name}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-zinc-500">{b.account_number}</td>
                <td className="px-2 py-1.5 text-[10px] font-mono text-right font-bold text-amber-700">Rp {formatIDR(b.net_amount)}</td>
                <td className="px-2 py-1.5 text-[10px] text-zinc-500">{b.processed_by}</td>
                <td className="px-2 py-1.5 text-[10px]">
                  {b.suntik_linked && b.suntik_match ? (
                    <div>
                      <span className={`font-semibold ${b.link_type === "tmp_masuk_saldo" ? "text-blue-700" : "text-emerald-700"}`}>{b.suntik_match.bank?.substring(0, 25)}</span>
                      <span className="text-zinc-400 ml-1">Rp {formatIDR(b.suntik_match.amount)}</span>
                      <span className="text-zinc-300 ml-1">({b.suntik_match.tab} r{b.suntik_match.row})</span>
                      {b.link_type === "tmp_masuk_saldo" && b.suntik_match.keterangan && (
                        <div className="text-[9px] text-blue-500 mt-0.5 truncate max-w-[200px]" title={b.suntik_match.keterangan}>{b.suntik_match.keterangan}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-zinc-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
        <Pager page={page} total={sorted.length} perPage={pp} onPage={setPage} />
      </div>
    </div>
  );
}

function SortTH({ label, field, current, dir, onSort, align = "left", color = "text-zinc-500" }) {
  const active = current === field;
  return (
    <th className={`text-${align} px-3 py-1.5 text-[9px] font-semibold uppercase ${color} cursor-pointer select-none hover:opacity-70`} onClick={onSort}>
      {label} {active && (dir === "asc" ? <ArrowUp size={10} className="inline ml-0.5" /> : <ArrowDown size={10} className="inline ml-0.5" />)}
    </th>
  );
}

function MC({ label, value, mono, color, testId, pulse, pulseVariant, pulseTitle }) {
  // pulseVariant: 'amber' (warning) | 'red' (urgent). Default amber.
  const variant = pulseVariant || "amber";
  const pulseClass = pulse
    ? (variant === "red" ? "animate-stat-pulse-red" : "animate-stat-pulse-amber")
    : "";
  const labelTone = pulse
    ? (variant === "red" ? "text-red-700" : "text-amber-700")
    : "text-zinc-500";
  const dotTone = pulse
    ? (variant === "red" ? "bg-red-500" : "bg-amber-500")
    : "";
  return (
    <div
      className={`border rounded-sm p-2 transition-shadow ${pulse ? pulseClass : "border-zinc-200"}`}
      data-testid={testId}
      title={pulse ? pulseTitle : undefined}
    >
      <p className={`text-[9px] font-semibold uppercase tracking-[0.15em] mb-0.5 flex items-center gap-1 ${labelTone}`}>
        {pulse && <span className={`inline-block h-1.5 w-1.5 rounded-full animate-ping ${dotTone}`} />}
        {label}
      </p>
      <div className={`text-xs ${mono ? "font-mono" : ""} ${color || "text-zinc-900"}`}>{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <select data-testid={testId} value={value} onChange={e => onChange(e.target.value)}
        className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs focus:ring-1 focus:ring-zinc-900 outline-none bg-white">
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function SortTh({ field, label, onSort, current, dir, align = "left" }) {
  const isActive = current === field;
  return (
    <th
      className={`${align === "right" ? "text-right" : "text-left"} px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] cursor-pointer select-none transition-colors ${isActive ? "text-zinc-900 bg-zinc-100" : "text-zinc-500 hover:text-zinc-700"}`}
      onClick={() => onSort(field)}
    >
      {label}
      {isActive && (dir === "asc" ? <ArrowUp size={9} className="inline ml-0.5" /> : <ArrowDown size={9} className="inline ml-0.5" />)}
    </th>
  );
}
